/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './ui/common_style.less';
import './ui/common_style.css';
import 'driver.js/dist/driver.min.css';
import svg_return from 'neuroglancer/static/return.svg';
import svg_home from 'neuroglancer/static/home.svg';
// import svg_filter from 'neuroglancer/static/filter.svg';
import svg_lasso from 'neuroglancer/static/lasso.svg';
import svg_mouse_move from 'neuroglancer/static/mouseMove.svg';
import svg_peizhun from 'neuroglancer/static/peizhun.svg';
import svg_lasso_load from 'neuroglancer/static/lasso_load.svg';
import svg_status_load from 'neuroglancer/static/status_load.svg';
// import svg_wuxianpeizhun from 'neuroglancer/static/wuxianpeizhun.svg';
import svg_peizhunmoban from 'neuroglancer/static/peizhunmoban.svg';
import svg_measure from 'neuroglancer/static/measure.svg';
// import svg_pizhu from 'neuroglancer/static/pizhu.svg';
import svg_reset from 'neuroglancer/static/reset.svg';
import svg_save from 'neuroglancer/static/save.svg';
import svg_load from 'neuroglancer/static/load.svg';
import svg_export from 'neuroglancer/static/export.svg';
import svg_settings from 'neuroglancer/static/settings.svg';
// import svg_book from 'ikonate/icons/book-opened.svg';
// import svg_controls_alt from 'ikonate/icons/controls-alt.svg';
// import svg_controls from 'ikonate/icons/controls.svg';
// import svg_image from 'ikonate/icons/image.svg';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import round from 'lodash/round';
import { CapacitySpecification, ChunkManager, ChunkQueueManager, FrameNumberCounter } from 'neuroglancer/chunk_manager/frontend';
import { makeCoordinateSpace, TrackableCoordinateSpace } from 'neuroglancer/coordinate_transform';
import { defaultCredentialsManager } from 'neuroglancer/credentials_provider/default_manager';
import { DataSource, DataSourceProviderRegistry } from 'neuroglancer/datasource';
import { getDefaultDataSourceProvider } from 'neuroglancer/datasource/default_provider';
import { StateShare, stateShareEnabled } from 'neuroglancer/datasource/state_share';
import { InputEventBindings as DataPanelInputEventBindings } from 'neuroglancer/data_panel_layout';
import { DisplayContext, TrackableWindowedViewport } from 'neuroglancer/display_context';
import { HelpPanelState, InputEventBindingHelpDialog } from 'neuroglancer/help/input_event_bindings';
import { addNewLayer, LayerManager, LayerSelectedValues, ManagedUserLayer, MouseSelectionState, SelectedLayerState, TopLevelLayerListSpecification, TrackableDataSelectionState, UserLayer } from 'neuroglancer/layer';
import { RootLayoutContainer } from 'neuroglancer/layer_groups_layout';
import { DisplayPose, NavigationState, OrientationState, Position, TrackableCrossSectionZoom, TrackableDepthRange, TrackableDisplayDimensions, TrackableProjectionZoom, TrackableRelativeDisplayScales, WatchableDisplayDimensionRenderInfo } from 'neuroglancer/navigation_state';
import 'neuroglancer/noselect.css';
import { Overlay, overlaysOpen } from 'neuroglancer/overlay';
import { allRenderLayerRoles, RenderLayerRole } from 'neuroglancer/renderlayer';
// import svg_gene from 'neuroglancer/static/gene.svg';
import { StatusMessage } from 'neuroglancer/status';
import { ElementVisibilityFromTrackableBoolean, TrackableBoolean } from 'neuroglancer/trackable_boolean';
import { makeDerivedWatchableValue, observeWatchable, TrackableValue, WatchableValueInterface } from 'neuroglancer/trackable_value';
import { LayerListPanel, LayerListPanelState, umapVisible } from 'neuroglancer/ui/layer_list_panel';
import { LayerSidePanelManager } from 'neuroglancer/ui/layer_side_panel';
import { setupPositionDropHandlers } from 'neuroglancer/ui/position_drag_and_drop';
import { SelectionDetailsPanel } from 'neuroglancer/ui/selection_details';
import { SidePanelManager } from 'neuroglancer/ui/side_panel';
import { StateEditorDialog } from 'neuroglancer/ui/state_editor';
import { StatisticsDisplayState, StatisticsPanel } from 'neuroglancer/ui/statistics';
import { ToolBinder } from 'neuroglancer/ui/tool';
import { ExportImageDialog } from 'neuroglancer/ui/export_image_dialog';
import { OpenFilesDialog } from 'neuroglancer/ui/open_files_dialog';
import { ViewerSettingsPanel, ViewerSettingsPanelState } from 'neuroglancer/ui/viewer_settings';
import { AutomaticallyFocusedElement } from 'neuroglancer/util/automatic_focus';
import { parseRGBColorSpecification, TrackableRGB } from 'neuroglancer/util/color';
import { Borrowed, Owned, RefCounted } from 'neuroglancer/util/disposable';
import { removeFromParent } from 'neuroglancer/util/dom';
import { ActionEvent, registerActionListener } from 'neuroglancer/util/event_action_map';
import { mat3, mat4, vec3 } from 'neuroglancer/util/geom';
import { parseFixedLengthArray, verifyFinitePositiveFloat, verifyObject, verifyOptionalObjectProperty, verifyPositiveInt, verifyString, expectArray } from 'neuroglancer/util/json';
import { EventActionMap, KeyboardEventBinder } from 'neuroglancer/util/keyboard_bindings';
import { NullarySignal } from 'neuroglancer/util/signal';
import { CompoundTrackable, optionallyRestoreFromJsonMember } from 'neuroglancer/util/trackable';
import { ViewerState, VisibilityPrioritySpecification, ConfigStorage } from 'neuroglancer/viewer_state';
import { WatchableVisibilityPriority } from 'neuroglancer/visibility_priority/frontend';
import { GL } from 'neuroglancer/webgl/context';
import { AnnotationToolStatusWidget } from 'neuroglancer/widget/annotation_tool_status';
import { makeIcon } from 'neuroglancer/widget/icon';
import { SelectView } from 'neuroglancer/widget/select_view';
import { ToolControl, ToolControlInput } from 'neuroglancer/widget/tool_control';

import {
  MousePositionWidget,
  CoronalPlaneNavigatorWidget,
  PositionWidget,
  ResolutionWidget
} from 'neuroglancer/widget/position_widget';
import { TrackableScaleBarOptions } from 'neuroglancer/widget/scale_bar';
import { RPC } from 'neuroglancer/worker_rpc';
import { allToolsButton, getNgLayers, hasCluster, local_dataId, local_host_SN_flag, viewer, viewer as _layoutviewer, userPemission, _check_image_Color, getNgLayersParam, setvisibleLayer, _param as local_href_param, _param, h5ad_file_binsize} from 'src/main';
import {AnnotationUserLayer, colorArr, formatterRGB1} from './annotation/user_layer';
import { genePanelState, makeElement, _checkColor, newGeneTabView } from './ui/layer_gene_table_tab';
import { proteinPanelState, proteinTabView } from './ui/layer_protein_tab';
import { cancellableFetchSpecialOk } from './util/special_protocol_request';
import './viewer.less';
import { message } from './widget/message';
import { LoadedDataSubsource, LoadedLayerDataSource } from './layer_data_source';
import { startRelativeMouseDrag } from './util/mouse_drag';
import { ImageUserLayer, RenderingOptionsTab, registParam } from './image_user_layer';
import {currentActivedTool1} from './ui/lasso'
import i18n from 'neuroglancer/i18n/i18n'
import { ConfigDialog, ConfigIndependDialog } from './ui/congfig-dialog';
import { AnnotationSource } from './annotation';
import { responseJson } from './util/http_request';
// import { CheckboxIcon } from './widget/checkbox_icon';
// import { StateEdit } from 'neuroglancer/ui/state_operate';
import { Lasso } from 'neuroglancer/ui/lasso';
import { SegmentDisplayTab } from './ui/segment_list';
import { SegmentationUserLayer } from './segmentation_user_layer';
import { RangeWidget } from 'neuroglancer/widget/range';
import { WatchableValue, WahtchableGlobalVisibleSegments, WahtchableGlobalStatedColor  } from 'neuroglancer/trackable_value';
import { SliceViewPanel } from 'neuroglancer/sliceview/panel';
import { TrackableImageTools } from './image_tools';
import { TwoLevelLayerWidget } from './ui/layer_bar';
import {quat} from "gl-matrix";
import { PemisionState } from './pemission';
import { LoadingProgress } from './ui/loadingProgress';
import { StateEdit } from './ui/state_operate';
import { CustomSegColorWidget, set16ToRgb } from './segmentation_display_state/frontend';
import {trackableAlphaValue} from "neuroglancer/trackable_alpha";
import {thousandth_formatter } from './util/comm_fun_hq';
import {MeasureTool} from "neuroglancer/ui/measureTool";
import {SharedWatchableValue} from "neuroglancer/shared_watchable_value";
import {HTTP_INTERCEPTORS_HEADER_CHANGED_ID, interceptorHeaderInfo} from "neuroglancer/util/http_interceptors_base";
import { registerLassoOpenDialog } from './ui/register_lasso_open_dialog';
export const notRotateModel = ['registration_with_track_line', 'registration_without_track_line']
import { tissueGefOutline } from './ui/tissue';
import { Uint64Set } from "neuroglancer/uint64_set";
import { Uint64 } from 'neuroglancer/util/uint64';
import {UmapMamager} from "neuroglancer/umap_mamager";
import {GeoRegistrationTool} from "neuroglancer/ui/geoRegistrationTool";
import { dbStorageMoreWindow } from 'neuroglancer/util/indexDb';
export let currentActivedTool: HTMLElement | null = null
export let allToolsControlDisable: Array<any>;
declare var NEUROGLANCER_OVERRIDE_DEFAULT_VIEWER_OPTIONS: any

interface CreditLink {
  url: string;
  text: string;
}

//扩展数据枚举
interface ProteinsFilter {
  cellPositionHashSet: Uint64Set | undefined,
  binSize: TrackableValue<string>,
  xAndYInfo: TrackableValue<Array<any>>,
  childWinArr: Array<any> | null,
}

declare var NEUROGLANCER_CREDIT_LINK: CreditLink | CreditLink[] | undefined;

const layerOriginTransforams = new Map<string, Float64Array>()
interface RegistParam {
  [key: string]: {
    OffsetX: number,
    OffsetY: number,
    rotateDeg?: number,
    zoomSize?: number,
  }
}

// interface imageTransformParam {
//   deg?: number,
//   scalex?: number,
//   scaley?: number,
//   bound: any,
//   filp?: number,
//   OffsetX?: number,
//   OffsetY?: number
// }

// const registParam: RegistParam = {}

// 创建虚拟区域
function createVirtualArea(className: string, positionArr: number[], width: number, height: number, parentId: string = 'neuroglancer-container'): void {
  const virtualArea = document.createElement('div')
  virtualArea.classList.add(className);
  virtualArea.style.position = 'absolute';
  if (positionArr[0]) { virtualArea.style.top = `${positionArr[0]}px` }
  if (positionArr[1]) { virtualArea.style.right = `${positionArr[1]}px` }
  if (positionArr[2]) { virtualArea.style.bottom = `${positionArr[2]}px` }
  if (positionArr[3]) { virtualArea.style.left = `${positionArr[3]}px` }
  virtualArea.style.width = `${width}px`;
  virtualArea.style.height = `${height}px`;
  document.getElementById(parentId)?.appendChild(virtualArea);
}

export class DataManagementContext extends RefCounted {
  worker: Worker;
  chunkQueueManager: ChunkQueueManager;
  chunkManager: ChunkManager;

  get rpc(): RPC {
    return this.chunkQueueManager.rpc!;
  }

  constructor(
    public gl: GL, public frameNumberCounter: FrameNumberCounter, bundleRoot: string = '') {
    super();
    const chunk_worker_url = bundleRoot + 'chunk_worker.bundle.js';
    this.worker = new Worker(chunk_worker_url);
    this.chunkQueueManager = this.registerDisposer(
      new ChunkQueueManager(new RPC(this.worker), this.gl, this.frameNumberCounter, {
        gpuMemory: new CapacitySpecification({ defaultItemLimit: 1e6, defaultSizeLimit: 1e9 }),
        systemMemory: new CapacitySpecification({ defaultItemLimit: 1e7, defaultSizeLimit: 2e9 }),
        download: new CapacitySpecification(
          { defaultItemLimit: 100, defaultSizeLimit: Number.POSITIVE_INFINITY }),
        compute: new CapacitySpecification({ defaultItemLimit: 128, defaultSizeLimit: 5e8 }),
      }));
    this.chunkQueueManager.registerDisposer(() => this.worker.terminate());
    this.chunkManager = this.registerDisposer(new ChunkManager(this.chunkQueueManager));
  }
}

export class InputEventBindings extends DataPanelInputEventBindings {
  global = new EventActionMap();
}

export const VIEWER_TOP_ROW_CONFIG_OPTIONS = [
  'showProteinButton',
  'showGeneButton',
  'showHelpButton',
  'showUseinfoButton',
  'showSettingsButton',
  'showEditStateButton',
  'showLayerListPanelButton',
  'showSelectionPanelButton',
  'showLayerSidePanelButton',
  'showLocation',
  'showAnnotationToolStatus',
] as const;

export const VIEWER_UI_CONTROL_CONFIG_OPTIONS = [
  ...VIEWER_TOP_ROW_CONFIG_OPTIONS,
  'showLayerPanel',
  'showLayerHoverValues',
] as const;

export const VIEWER_UI_CONFIG_OPTIONS = [
  ...VIEWER_UI_CONTROL_CONFIG_OPTIONS,
  'showUIControls',
  'showPanelBorders',
] as const;

export type ViewerUIOptions = {
  [Key in (typeof VIEWER_UI_CONFIG_OPTIONS)[number]]: boolean
};

export type ViewerUIConfiguration = {
  [Key in (typeof VIEWER_UI_CONFIG_OPTIONS)[number]]: TrackableBoolean;
};

export function makeViewerUIConfiguration(): ViewerUIConfiguration {
  return Object.fromEntries(VIEWER_UI_CONFIG_OPTIONS.map(
    key => [key, new TrackableBoolean(true)])) as ViewerUIConfiguration;
}

function setViewerUiConfiguration(
  config: ViewerUIConfiguration, options: Partial<ViewerUIOptions>) {
  for (const key of VIEWER_UI_CONFIG_OPTIONS) {
    const value = options[key];
    if (value !== undefined) {
      config[key].value = value;
    }
  }
}
// let anno_shader = '';

export interface ViewerOptions extends ViewerUIOptions, VisibilityPrioritySpecification {
  dataContext: Owned<DataManagementContext>;
  element: HTMLElement;
  dataSourceProvider: Borrowed<DataSourceProviderRegistry>;
  uiConfiguration: ViewerUIConfiguration;
  showLayerDialog: boolean;
  inputEventBindings: InputEventBindings;
  resetStateWhenEmpty: boolean;
  bundleRoot: string;
}

const defaultViewerOptions = 'undefined' !== typeof NEUROGLANCER_OVERRIDE_DEFAULT_VIEWER_OPTIONS ?
  NEUROGLANCER_OVERRIDE_DEFAULT_VIEWER_OPTIONS :
  {
    showLayerDialog: true,
    resetStateWhenEmpty: true,
  };

class TrackableViewerState extends CompoundTrackable {
  constructor(public viewer: Borrowed<Viewer>) { // this
    super();
    this.add('title', viewer.title);
    this.add('dimensions', viewer.coordinateSpace);
    this.add('relativeDisplayScales', viewer.relativeDisplayScales);
    this.add('displayDimensions', viewer.displayDimensions);
    this.add('position', viewer.position);
    this.add('crossSectionOrientation', viewer.crossSectionOrientation);
    this.add('crossSectionScale', viewer.crossSectionScale);
    this.add('crossSectionDepth', viewer.crossSectionDepthRange);
    this.add('projectionOrientation', viewer.projectionOrientation);
    this.add('projectionScale', viewer.projectionScale);
    this.add('projectionDepth', viewer.projectionDepthRange);
    this.add('layers', viewer.layerSpecification);
    this.add('showAxisLines', viewer.showAxisLines);
    this.add('wireFrame', viewer.wireFrame);
    // this.add('showScaleBar', viewer.showScaleBar);
    this.add('showDefaultAnnotations', viewer.showDefaultAnnotations);

    this.add('showSlices', viewer.showPerspectiveSliceViews);
    this.add('gpuMemoryLimit', viewer.dataContext.chunkQueueManager.capacities.gpuMemory.sizeLimit);
    this.add('prefetch', viewer.dataContext.chunkQueueManager.enablePrefetch);
    this.add(
      'systemMemoryLimit',
      viewer.dataContext.chunkQueueManager.capacities.systemMemory.sizeLimit);
    this.add(
      'concurrentDownloads', viewer.dataContext.chunkQueueManager.capacities.download.itemLimit);
    this.add('selectedLayer', viewer.selectedLayer);
    this.add('proteinLayer', viewer.proteinPanelState);
    this.add('crossSectionBackgroundColor', viewer.crossSectionBackgroundColor);
    this.add('projectionBackgroundColor', viewer.perspectiveViewBackgroundColor);
    this.add('layout', viewer.layout);
    this.add('statistics', viewer.statisticsDisplayState);
    this.add('helpPanel', viewer.helpPanelState);
    this.add('settingsPanel', viewer.settingsPanelState);
    this.add('geneTablePanel', viewer.genePanelState);
    this.add('selection', viewer.selectionDetailsState);
    this.add('layerListPanel', viewer.layerListPanelState);
    this.add('partialViewport', viewer.partialViewport);
    this.add('selectedStateServer', viewer.selectedStateServer);
    this.add('showThumbnail', viewer.showThumbnail);
    this.add('geneTableUrl', viewer.geneTableUrl);
    this.add('selectedGenes', viewer.selectedGenes);
    this.add('showSingleGene', viewer.showSingleGene);
    this.add('isTransform', viewer.isTransform);
    this.add('colorBar',new TrackableValue(viewer.color_scale, verifyObject) );
    this.add('imageTools', viewer.imageTools);
    this.add('binSize', viewer.binSize);
    // this.add('proteinMergeColor', viewer.proteinMergeColor);
    this.add('self_newlabel',new TrackableValue( viewer.self_newlabel, verifyObject ));
    this.add('bin200_self_newlabel',new TrackableValue( viewer.bin200_self_newlabel, verifyObject ));
    this.add('globalStatedColor', new TrackableValue(viewer.globalStatedColor, verifyObject));
    this.add('self_newlabel_color', new TrackableValue(viewer.self_newlabel_color, verifyObject));
    this.add('bin200_self_newlabel_color', new TrackableValue(viewer.bin200_self_newlabel_color, verifyObject));
    this.add('imgOpacityFactor', viewer.imgOpacityFactor);
    this.add('cellShowMode', viewer.cellShowMode);
    this.add('outLineColor', viewer.outLineColor);
  }

  restoreState(obj: any, flag: boolean = true) {
    this.viewer.configStorage.clear();
    const { viewer } = this;
    if(flag)viewer.preRestoreState()

    const json_ = viewer.state.toJSON();

    const statusPosition = obj.position;//导入状态的position参数长度和当前position长度保持一致。start
    const curPosition = json_.position;
    if (curPosition && statusPosition) {
      let resultPosition = [];
      for (let i= 0, len = curPosition.length; i < len; i++) {
        if (statusPosition[i]) {
          resultPosition.push(statusPosition[i]);
        } else {
          resultPosition.push(0.5);
        }
      }
      obj.position = resultPosition;
    }//导入状态的position参数长度和当前position长度保持一致。end

    super.restoreState(obj);
    if (viewer.measureTool) {
      viewer.measureTool.dispose();
      viewer.measureTool = undefined;
    }
    // 初始化的时候同时初始化进度条，避免第一个chunk请求完进度条才开始显示加载
    new LoadingProgress(viewer)
    // Handle legacy properties
    verifyOptionalObjectProperty(obj, 'navigation', navObj => {
      verifyObject(navObj);
      verifyOptionalObjectProperty(navObj, 'pose', poseObj => {
        verifyObject(poseObj);
        verifyOptionalObjectProperty(poseObj, 'position', positionObj => {
          verifyObject(positionObj);
          optionallyRestoreFromJsonMember(positionObj, 'voxelCoordinates', viewer.position);
          verifyOptionalObjectProperty(positionObj, 'voxelSize', voxelSizeObj => {
            // Handle legacy voxelSize representation
            const voxelSize =
              parseFixedLengthArray(new Float64Array(3), voxelSizeObj, verifyFinitePositiveFloat);
            for (let i = 0; i < 3; ++i) {
              voxelSize[i] *= 1e-9;
            }
            viewer.coordinateSpace.value = makeCoordinateSpace({
              valid: false,
              names: ['x', 'y', 'z'],
              units: ['m', 'm', 'm'],
              scales: voxelSize,
            });
          });
        });
        optionallyRestoreFromJsonMember(poseObj, 'orientation', viewer.crossSectionOrientation);
      });
      optionallyRestoreFromJsonMember(
        navObj, 'zoomFactor', viewer.crossSectionScale.legacyJsonView);
    });
    optionallyRestoreFromJsonMember(obj, 'perspectiveOrientation', viewer.projectionOrientation);
    optionallyRestoreFromJsonMember(obj, 'perspectiveZoom', viewer.projectionScale.legacyJsonView);
    optionallyRestoreFromJsonMember(
      obj, 'perspectiveViewBackgroundColor', viewer.perspectiveViewBackgroundColor);
  }
}

export interface arrayObject {
  [k: string] : string | number | Array<string | number | {[K : string] : string | number}> | {} | any | unknown
}


export interface ChunkStatistics {
  totalPresent: TrackableValue<number>,
  totalChunk: TrackableValue<number>
}
export class Viewer extends RefCounted implements ViewerState {
  chunkStatistics = {
    totalPresent: new TrackableValue<number>( 0, verifyPositiveInt),
    totalChunk: new TrackableValue<number>( 0, verifyPositiveInt)
  }
  pemission = new PemisionState(userPemission)
  initScale: number; // 初始化比例
  initStateJson: any;  // 初始化JSON,加载状态时会更新该json
  globalVisibleSegments = new WahtchableGlobalVisibleSegments(); // 全局cluster，包含选中的ids和自定义的颜色映射表
  globalStatedColor = new WahtchableGlobalStatedColor(); // 全局cluster，包含选中的ids和自定义的颜色映射表
  parentMap = new Map<string, TwoLevelLayerWidget | HTMLElement>(); // 用来存放图层菜单目录
  groupLayerMap = new Map<string, ManagedUserLayer>(); // 用来存放分屏所有的layer
  showSingleGene = new TrackableValue<string | undefined>( undefined, verifyString);   //  用于seg-cell是否展示颜色弹窗
  selectedGenes = new TrackableValue( {},  verifyObject);        //  蛋白基因
  geneTableUrl = new TrackableValue<string | undefined>(undefined, verifyString);
  isTransform = new TrackableBoolean(false, false);
  title = new TrackableValue<string | undefined>(undefined, verifyString);
  coordinateSpace = new TrackableCoordinateSpace();
  position = this.registerDisposer(new Position(this.coordinateSpace));
  relativeDisplayScales =
    this.registerDisposer(new TrackableRelativeDisplayScales(this.coordinateSpace));
  displayDimensions = this.registerDisposer(new TrackableDisplayDimensions(this.coordinateSpace));
  displayDimensionRenderInfo = this.registerDisposer(new WatchableDisplayDimensionRenderInfo(
    this.relativeDisplayScales.addRef(), this.displayDimensions.addRef()));
  crossSectionOrientation = this.registerDisposer(new OrientationState());
  crossSectionScale = this.registerDisposer(
    new TrackableCrossSectionZoom(this.displayDimensionRenderInfo.addRef()));
  projectionOrientation = this.registerDisposer(new OrientationState());
  crossSectionDepthRange =
    this.registerDisposer(new TrackableDepthRange(-10, this.displayDimensionRenderInfo));
  projectionDepthRange =
    this.registerDisposer(new TrackableDepthRange(-50, this.displayDimensionRenderInfo));
  projectionScale =
    this.registerDisposer(new TrackableProjectionZoom(this.displayDimensionRenderInfo.addRef()));
  navigationState = this.registerDisposer(new NavigationState(
    new DisplayPose(
      this.position.addRef(), this.displayDimensionRenderInfo.addRef(),
      this.crossSectionOrientation.addRef()),
    this.crossSectionScale.addRef(), this.crossSectionDepthRange.addRef()));
  perspectiveNavigationState = this.registerDisposer(new NavigationState(
    new DisplayPose(
      this.position.addRef(), this.displayDimensionRenderInfo.addRef(),
      this.projectionOrientation.addRef()),
    this.projectionScale.addRef(), this.projectionDepthRange.addRef()));
  mouseState = new MouseSelectionState();
  layerManager = this.registerDisposer(new LayerManager());
  selectedLayer = this.registerDisposer(new SelectedLayerState(this.layerManager.addRef()));
  showAxisLines = new TrackableBoolean(false, false);
  wireFrame = new TrackableBoolean(false, false);
  // 工具控制
  showScaleBar = new TrackableBoolean(true, true);
  isLegendOfImage = new TrackableBoolean(true, true);
  showLegend:TrackableBoolean = new TrackableBoolean(true, true);
  showParameter = new TrackableBoolean(false, false);
  showStatisticsInfo = new TrackableBoolean(true, true);
  showThumbnail = new TrackableBoolean(true, true);
  showColorbar = new TrackableBoolean(false, false);
  showHoverInfo = new TrackableBoolean(true, true);
  imageTools = new TrackableImageTools(this);
  proteinMergeColor = new TrackableBoolean(false, false);   //  genetableselected组默认为蛋白merge
  isBinsizeAuto = new TrackableBoolean(false, false);     // 是否开启自动切换bin
  scale = new WatchableValue(1);                          // 左侧缩放系数
  binSize = new TrackableValue<string | undefined>('bin50', verifyString);
  cluterView: SelectView;
  curSegmentDisplayTab: SegmentDisplayTab;
  showPerspectiveSliceViews = new TrackableBoolean(true, true);
  visibleLayerRoles = allRenderLayerRoles();
  showDefaultAnnotations = new TrackableBoolean(true, true);
  crossSectionBackgroundColor = new TrackableRGB(vec3.fromValues(0.121, 0.129, 0.196));
  perspectiveViewBackgroundColor = new TrackableRGB(vec3.fromValues(0, 0, 0));
  scaleBarOptions = new TrackableScaleBarOptions();
  partialViewport = new TrackableWindowedViewport();
  statisticsDisplayState = new StatisticsDisplayState();
  helpPanelState = new HelpPanelState();
  cellShowMode = new TrackableValue<string | undefined>('filled', verifyString);
  outLineColor = new TrackableValue<string | undefined>('Cluster', verifyString);
  settingsPanelState = new ViewerSettingsPanelState();
  genePanelState = new genePanelState();
  proteinPanelState = new proteinPanelState();
  genePanel: newGeneTabView| undefined = undefined;
  layerSelectedValues =
    this.registerDisposer(new LayerSelectedValues(this.layerManager, this.mouseState));
  selectionDetailsState = this.registerDisposer(
    new TrackableDataSelectionState(this.coordinateSpace, this.layerSelectedValues));
  selectedStateServer = new TrackableValue<string>('', verifyString);
  imgOpacityFactor = trackableAlphaValue(0.4);
  imgFadeNeeded = new TrackableBoolean(false, false);
  layerListPanelState = new LayerListPanelState();

  resetInitiated = new NullarySignal();

  proteinsFilter: ProteinsFilter = {
    cellPositionHashSet: undefined,
    binSize: new TrackableValue<string>('', verifyString),
    xAndYInfo: new TrackableValue( [],  expectArray),
    childWinArr: [],
  };

  configStorage = new ConfigStorage()
  configDialog: ConfigDialog | null;
  layerTree: Array<any>;
  allToolsControlDisable: Array<any> = [];
  isMoving: boolean = false;
  private resetJson: any;
  measureTool: MeasureTool|undefined;
  geoRegistrationTool: GeoRegistrationTool|undefined;

  get chunkManager() {
    return this.dataContext.chunkManager;
  }
  get chunkQueueManager() {
    return this.dataContext.chunkQueueManager;
  }

  layerSpecification: TopLevelLayerListSpecification;
  layout: RootLayoutContainer;
  sidePanelManager: SidePanelManager;

  state: TrackableViewerState;

  dataContext: Owned<DataManagementContext>;
  visibility: WatchableVisibilityPriority;
  inputEventBindings: InputEventBindings;
  element: HTMLElement;
  dataSourceProvider: Borrowed<DataSourceProviderRegistry>;
  dataSource: DataSource;    //  只存储annotation
  uiConfiguration: ViewerUIConfiguration;
  maxValue: number;
  color_scale: {
    [K: string] : {
      defaultValue: number,
      minValue: number,
      maxValue: number,
      isAutoScaleFlag: boolean,
    }
  };
  resolution_parameter_value: {[K:string]: string} = {};
  transform_sign_point: vec3 = vec3.create();
  imageSourceModelMat4Inverse: mat4 = mat4.create();
  imageSourceModelMat4: mat4 = mat4.create();
  transformsize: number | string = 5;
  self_newlabel: {[K:string]: any} = {};
  bin200_self_newlabel: {[K:string]: any} = {};
  self_newlabel_color: any = {};
  bin200_self_newlabel_color: {[K:string]: any} = {};
  saveSelectedKeyVal:{[K:string]: any} = {};
  /**
   * bin200_self_newlabel_color = { h5adfile: { bin200: { cluster: color } } }
   */
  allDataSource: arrayObject = {
    colorscale: {
      dir: '',
      color: [],
      colorStr: 'vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))',
      heatmapShader: `#uicontrol float sizeFactor slider(min=0.0, max=1.0, default=1.0, step=0.01); 
      #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
      void main() { 
        setColor(colormapNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));
        setPointMarkerFactor(sizeFactor);
        setPointMarkerOpacityFactor(oFactor - 0.01);
      }`,
      genecolor: 'Default',
      colorData:[0.05,0.20,0.51, 0.00,0.37,0.64, 0.04,0.53,0.73,
        0.00,0.76,0.60,0.95,0.83,0.22,0.96,0.69,0.20,
        0.95,0.56,0.22,0.91,0.38,0.14,0.85,0.12,0.12,
        0.85,0.12,0.12]
    },
    allDataSource: [],
    imageType: [],    // 存储image类型（image{ type: 'ssDNA', num_channel: 1 }）
    proteinIF: [],
    segmentlist: {
      list: [],
      order: '',
      label: ''
    },   // 存储segmentlist
    changeProteinColor: {}
  };   //  存储所有的annotation
  image_rotate: string = '';
  statisticsInfo:any = {};
  h5adfile_cellbinfile:any = {};   //  存储h5ad file和cellbin file文件，多个cellbin 和h5ad
  h5adfile_info_res: any = {};     //  存储h5ad 的聚类信息，用于聚类列表和图例的展示
  binsizeMaxvalue:any = {};
  spliceBounds: any = {};
  registParam: RegistParam = {}
  threeDmaxValue: any = {}; // 存储fixpoint对应的maxexps
  // layerCloneObj: any = [];
  layerCloneObj = {layer: [], layout: 'xy', gene_list_file: allToolsButton.genelistFile};
  jsonCloneObj: any = [];   //  返回上一步json
  emptyChunks: any = {};
  renderCompleted = new TrackableBoolean(false, true); //渲染是否完成
  homeCrossSectionScale = 1;
  minScaleFactor = 0.8;
  initCrossSectionScaleForDemoData: number;
  navigatorInitialized = false;
  initBoundSize: any;
  tissueGefOutline:tissueGefOutline | undefined = undefined;    //  tissueGefOutline类，初始化一个，每次更新轮廓即可
  tissueGefOutlineGeoData:any = {};      //  存储gef对应的tissue轮廓信息
  private makeUiControlVisibilityState(key: keyof ViewerUIOptions) {
    const showUIControls = this.uiConfiguration.showUIControls;
    const option = this.uiConfiguration[key];
    return this.registerDisposer(
      makeDerivedWatchableValue((a, b) => a && b, showUIControls, option));
  }

  /**
   * Logical and of each `VIEWER_UI_CONTROL_CONFIG_OPTIONS` option with the value of showUIControls.
   */
  uiControlVisibility: {
    [key in (typeof VIEWER_UI_CONTROL_CONFIG_OPTIONS)[number]]: WatchableValueInterface<boolean>
  } = <any>{};

  showLayerDialog: boolean;
  resetStateWhenEmpty: boolean;
  needRestoreHeatMapFilter: boolean = true;//重置heatMap 过滤标志，初次加载，以及切换斌Size后需要重置
  umapMamager: UmapMamager = new UmapMamager();

  get inputEventMap() {
    return this.inputEventBindings.global;
  }

  visible = true;
  filesInfoFormatted: any;
  openFilesDialog: OpenFilesDialog
  lassoTool: Lasso | undefined
  workflowManualtoolsDialog: registerLassoOpenDialog; 
  constructor(public display: DisplayContext, options: Partial<ViewerOptions> = {}) {
    super();

    const {
      dataContext = new DataManagementContext(display.gl, display, options.bundleRoot),
      visibility = new WatchableVisibilityPriority(WatchableVisibilityPriority.VISIBLE),
      inputEventBindings = {
        global: new EventActionMap(),
        sliceView: new EventActionMap(),
        perspectiveView: new EventActionMap(),
      },
      element = display.makeCanvasOverlayElement(),
      dataSourceProvider =
      getDefaultDataSourceProvider({ credentialsManager: defaultCredentialsManager }),
      uiConfiguration = makeViewerUIConfiguration(),
    } = options;
    const test = SharedWatchableValue.make(dataContext.rpc,{'init':12})
    dataContext.rpc!.invoke(HTTP_INTERCEPTORS_HEADER_CHANGED_ID, {id: test.rpcId, value: interceptorHeaderInfo.value});
    this.visibility = visibility;
    this.inputEventBindings = inputEventBindings;
    this.element = element;
    this.dataSourceProvider = dataSourceProvider;
    this.uiConfiguration = uiConfiguration;
    const {activedTool, parentStorage, configStorage} = this.configStorage
    allToolsControlDisable = this.allToolsControlDisable;
    activedTool.changed.add(() => {
      const currentActivedTool = activedTool.value;
      if(currentActivedTool && this.configStorage.isReady(currentActivedTool) === undefined) viewer.configStorage.registConfig(i18n.color_bar, document.getElementsByClassName('tool-control-'+i18n.color_bar.replace(/\s*/g,""))[0] as HTMLElement, i18n.color_bar);
      if (currentActivedTool && this.configStorage.isReady(currentActivedTool)) {
        this.configDialog = new ConfigDialog(parentStorage.get(currentActivedTool), configStorage.get(currentActivedTool), this.configStorage, {position: this.configDialog?.position, className: 'layer-viewer-config'})
      } else if (!currentActivedTool) {
        if (this.configDialog) {
          this.configDialog.close()
          this.configDialog = null
          // this.selectedLayer.layer = undefined
        }
      }
    })
    //细胞绘制方式修改后，重新绘制
    this.cellShowMode.changed.add(()=>{
      display.scheduleRedraw();
    });
    this.outLineColor.changed.add(()=>{
      display.scheduleRedraw();
    });
    //监听视口中心变化，视口中心位置移动时，可以用低精度模型渲染。移动完成后再用高精度模型渲染
    this.navigationState.pose.changed.add(
      throttle(() => {
        this.isMoving = true;
      },100,{leading:true})
    );
    this.navigationState.pose.changed.add(
      debounce(() => {
        this.isMoving = false;
        display.scheduleRedraw();
      },200)
    );

    this.proteinsFilter.xAndYInfo.changed.add(() => {
      this.proteinsFilter.cellPositionHashSet?.clear();
      if(this.proteinsFilter.xAndYInfo?.value){
        this.proteinsFilter.xAndYInfo?.value[0].forEach((i: number, index: number) => {
          this.proteinsFilter.cellPositionHashSet?.add(new Uint64(i, this.proteinsFilter.xAndYInfo.value[1][index]));
        })
      }
      display.scheduleRedraw();
    })

    this.proteinsFilter.binSize.changed.add(() => {
      changeBinSize(this.proteinsFilter.binSize.value);
    })

    this.globalVisibleSegments.visibleSegments.changed.add(()=>{
      if (this.globalVisibleSegments.visibleSegments.hashTable.size > 0 && this.binSize.value === 'cellbin') {
        this.imgFadeNeeded.value = true;
      } else {
        this.imgFadeNeeded.value = false;
      }
    })
    this.layerManager.layersVisibleChanged.add(() => {
      let imgFadeNeeded = false
      if (this.globalVisibleSegments.visibleSegments.hashTable.size > 0 && this.binSize.value === 'cellbin') {
        const {managedLayers} = this.layerManager
        for (let i = 0; i < managedLayers.length; i++) {
          if(managedLayers[i].name.indexOf('cluster') > -1 && managedLayers[i].visible) {
            imgFadeNeeded = true;
            break;
          }
        }
      }
      this.imgFadeNeeded.value = imgFadeNeeded;
    })
    this.registerDisposer(observeWatchable(value => {
      this.display.applyWindowedViewportToElement(element, value);
    }, this.partialViewport));

    this.registerDisposer(() => removeFromParent(this.element));

    this.dataContext = this.registerDisposer(dataContext);

    setViewerUiConfiguration(uiConfiguration, options);

    const optionsWithDefaults = { ...defaultViewerOptions, ...options };
    const {
      resetStateWhenEmpty,
      showLayerDialog,
    } = optionsWithDefaults;

    for (const key of VIEWER_UI_CONTROL_CONFIG_OPTIONS) {
      this.uiControlVisibility[key] = this.makeUiControlVisibilityState(key);
    }
    this.registerDisposer(this.uiConfiguration.showPanelBorders.changed.add(() => {
      this.updateShowBorders();
    }));

    this.showLayerDialog = showLayerDialog;
    this.resetStateWhenEmpty = resetStateWhenEmpty;

    this.layerSpecification = new TopLevelLayerListSpecification(
      this.display, this.dataSourceProvider, this.layerManager, this.chunkManager,
      this.selectionDetailsState, this.selectedLayer, this.navigationState.coordinateSpace,
      this.navigationState.pose.position, this.toolBinder);

    this.registerDisposer(display.updateStarted.add(() => {
      this.onUpdateDisplay();
    }));

    this.showDefaultAnnotations.changed.add(() => {
      if (this.showDefaultAnnotations.value) {
        this.visibleLayerRoles.add(RenderLayerRole.DEFAULT_ANNOTATION);
      } else {
        this.visibleLayerRoles.delete(RenderLayerRole.DEFAULT_ANNOTATION);
      }
    });

    this.registerDisposer(this.navigationState.changed.add(() => {
      this.handleNavigationStateChanged();
    }));

    // Debounce this call to ensure that a transient state does not result in the layer dialog being
    // shown.
    const maybeResetState = this.registerCancellable(debounce(() => {
      if (!this.wasDisposed && this.layerManager.managedLayers.length === 0 &&
        this.resetStateWhenEmpty) {
        // No layers, reset state.
        this.navigationState.reset();
        this.perspectiveNavigationState.pose.orientation.reset();
        this.perspectiveNavigationState.zoomFactor.reset();
        this.resetInitiated.dispatch();
        if (!overlaysOpen && this.showLayerDialog && this.visibility.visible) {
          addNewLayer(this.layerSpecification, this.selectedLayer);
        }
      }
    }));
    this.layerManager.layersChanged.add(maybeResetState);
    maybeResetState();

    // this.layerManager.layersChanged.add(debounce(()=>{//初始化geneHeatMap 过滤最大值
    //   viewer.updateGeneHeatMapFilter(true);
    // },500))

    this.registerDisposer(this.dataContext.chunkQueueManager.visibleChunksChanged.add(() => {
      this.layerSelectedValues.handleLayerChange();
    }));

    this.registerDisposer(this.dataContext.chunkQueueManager.visibleChunksChanged.add(() => {
      if (this.visible) {
        display.scheduleRedraw();
      }
    }));

    this.makeUI();
    this.updateShowBorders();


    this.registerActionListeners();
    this.registerEventActionBindings();

    this.registerDisposer(setupPositionDropHandlers(element, this.navigationState.position));

    this.state = new TrackableViewerState(this);
  }

  updateGeneHeatMapFilter(updataAnyway:boolean = false){
    if (viewer.needRestoreHeatMapFilter || updataAnyway) {
      viewer.needRestoreHeatMapFilter = false;
      viewer.layerManager.layerSet.forEach(item=>{
        if (item.genetype === 'annotationdnb') {
          const shaderControls = (item.layer as AnnotationUserLayer).annotationDisplayState.shaderControls;
          shaderControls.restoreState({
            'midFilter': {
              'range': [0, viewer.color_scale[allToolsButton.genelistFile]?.defaultValue ?? 0],
              'window': [0, viewer.color_scale[allToolsButton.genelistFile]?.defaultValue ?? 0],
            }
          })
        }
      })
    }
  }
  private updateShowBorders() {
    const { element } = this;
    const className = 'neuroglancer-show-panel-borders';
    if (this.uiConfiguration.showPanelBorders.value) {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }
  }

  clearActiveTool (value?: HTMLElement | null) {
    currentActivedTool = value ? value : null
  }
  // restoreState前需要处理的逻辑
  preRestoreState () {
    // if (this.binSize.value === 'cellbin') {
    //   // 初始化聚类
    //   const {element} = this.cluterView
    //   if (element && element.children.length > 1) {
    //     element.removeChild(element.children[1])
    //     this.cluterView.setState(false)
    //   }
    // }
    // 初始化聚类
    this.cluterView.setState(false)
    this.curSegmentDisplayTab?.dispose();

    layerOriginTransforams.clear()

    // UMAPlayout判定
    /*const {layers} = obj
    const newLayout:any = {
      "type": "row",
      "children": [
        {
          "type": "viewer",
          "layers": [],
          "layout": "xy",
          "panelType": '',
        }
      ]
    }
    if(layers?.length > 0){
      for (let key of layers) {
        if (key.name.includes('UMAP') && key.visible) {
          newLayout.children.push({
            "type": "viewer",
            "panelType": 'UMAP',
            "position": {
              "link": "unlinked",
            },
            "crossSectionOrientation": {
              "link": "unlinked",
            },
            "crossSectionScale": {
              "link": "unlinked",
              "value": 50,
            },
            "layers": [
              key.name
            ],
            "layout": "xy"
          })
        } else {
          newLayout.children[0].layers.push(key.name)
        }
      }
    }
    if (newLayout.children.length > 1) {
      obj.layout = newLayout
    } else {
      obj.layout = 'xy';
    }
    console.log(newLayout)*/
  }

  // getLayout (layers: Array<ManagedUserLayer>) {
  //   const newLayout = {
  //     "type": "row",
  //     "children": [
  //       {
  //         "type": "viewer",
  //         "layers": [
  //           `ssDNA`
  //         ],
  //         "layout": "xy",
  //         "panelType": '',
  //       }
  //     ]
  //   }
  //   for (let key of layers) {
  //     if (key.name.includes('UMAP') && key.visible) {
  //       newLayout.children.push({
  //         "type": "viewer",
  //         "panelType": 'UMAP',
  //         "layers": [
  //           `UMAP`
  //         ],
  //         "layout": "xy"
  //       })
  //     } else {
  //       newLayout.children[0].layers.push(key.name)
  //     }
  //   }
  //   if (newLayout.children.length > 1) {
  //     return newLayout
  //   } else {
  //     return 'xy';
  //   }
  // }
  updateScaleBarBinSize(){
    let visibleAnnoLayer = this.layerManager.managedLayers.find((layer)=>{
      return layer.visible && layer.type=== "annotation"
    });
    if (!!visibleAnnoLayer) {
      this.scaleBarOptions.value.binSize = this.binSize.value;
    } else {
      this.scaleBarOptions.value.binSize = undefined;
    }
  }
  private makeUI() {

    const gridContainer = this.element;
    gridContainer.classList.add('neuroglancer-viewer');
    gridContainer.classList.add('neuroglancer-noselect');
    gridContainer.style.display = 'flex';
    gridContainer.style.flexDirection = 'column';

    const topRow = document.createElement('div');
    topRow.classList.add('neuroglancer-viewer-top-row');
    topRow.style.display = 'flex';
    topRow.style.flexDirection = 'row';
    topRow.style.alignItems = 'stretch';

    if (local_href_param.flag ===  "tool" && !local_href_param.isReadOnly) {
        const filesKey = local_href_param.DataID + 'repeatFiles'
        const repeatFileStr = sessionStorage.getItem(filesKey);
        if (repeatFileStr) {
          sessionStorage.removeItem(filesKey);
          const repeatFiles = JSON.parse(repeatFileStr);
          let repeatFileInfoList: string[] = [];
          repeatFiles.forEach((item:any)=>{
            let info = i18n.taskId + item.file_name + i18n.fileName + item.task_id + i18n.openedRepeatedly;
            repeatFileInfoList.push(info);
          })
          new message(repeatFileInfoList, 'info');
        }
        const openFileWidget = makeElement('div',['open-files-wrap'],{}, svg_load);
        openFileWidget.appendChild(makeElement('span',['open-files-label'],{}, i18n.open));
        if (local_href_param.DataID)topRow.appendChild(openFileWidget);

        const dropdownUl = makeElement('ul', ['el-select-dropdown__list', 'open-files-dropdown']);
        const newFilesLi = makeElement('li', ['el-select-dropdown_item'], {});
        const newFilesSpan = makeElement('span', [], {}, i18n.newFiles);
        newFilesLi.appendChild(newFilesSpan);
        dropdownUl.appendChild(newFilesLi);
        const addFilesLi = makeElement('li', ['el-select-dropdown_item'], {});
        const addFilesSpan = makeElement('span', [], {}, i18n.addFiles);

        newFilesLi.addEventListener('click',()=>{
          this.openFilesDialog = new OpenFilesDialog(false);
        })
        addFilesLi.addEventListener('click',()=>{
          this.openFilesDialog = new OpenFilesDialog(true);
        })
        addFilesLi.appendChild(addFilesSpan);
        dropdownUl.appendChild(addFilesLi);
        openFileWidget.appendChild(dropdownUl);
      // }
    }
    window.addEventListener(
      "message",
      (event) => {
        if ("tableDataFromSelectFile" === event.data.key) {//判断信息来源
          const filesInfo = JSON.parse(event.data.data);
          let isValid = true;
          let inValidFileNameArr = ['Invalid files:'];
          if (filesInfo.length > 0) {
            const filesInfoFormatted = {
              file: new Array(),//当交互式工具不能读取到DMZ路径时启用
              file_info: new Array(),
              folder: new Array()
            }
            for (let i = 0,len = filesInfo.length; i < len; i++ ){
              let item = filesInfo[i]
              if (item.dataType === '0') {
                // const fileInfo = {
                //   meta_data_id: item.metadataId,
                //   name: item.name,
                //   sn: item.sampleId,
                // }
                if (item.name.endsWith('tissue.gef') || item.name.endsWith('raw.gef')) {// tissue.gef和raw.gef校验
                  isValid = false;
                  inValidFileNameArr.push(item.name);
                }
                const fileInfoWithPath = {
                  path: item.path,
                  name: item.name,
                  sample_id: item.sampleId,
                  process_id: item.wfTag,
                  task_id: item.taskId,
                  menu_path: `${item.menuPath}/${item.searchName}`
                }
                // filesInfoFormatted.file.push(fileInfo);
                filesInfoFormatted.file_info.push(fileInfoWithPath);
              } else {
                const folderInfo = {
                  id: item.id,
                  sn: item.searchName,
                }
                filesInfoFormatted.folder.push(folderInfo);
              }
            }
            if (isValid) {
              this.filesInfoFormatted = filesInfoFormatted;
              this.openFilesDialog.setConfirmBtnEnable(true);
            } else {
              new message(inValidFileNameArr, 'info');
              this.openFilesDialog.setConfirmBtnEnable(false);
            }
          } else {
            this.openFilesDialog.setConfirmBtnEnable(false);
          }
        }else if(event.data.key === 'workflow_manualtools') {
          console.log(event.data.key)
          if(event.data.data === 'close'){
            // 关闭
            this.workflowManualtoolsDialog.dispose();
          }else if(event.data.data === 'loaded'){
            this.workflowManualtoolsDialog.is_loaded.value = true;
          }
        }
      },
      false,
    );
    const resolutionWidget = this.registerDisposer(new ResolutionWidget(
      document.createElement('div'), this.mouseState, this.navigationState.coordinateSpace));
    // resolutionWidget.element.style.alignSelf = 'center';
    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      this.uiControlVisibility.showLocation, resolutionWidget.element));
    topRow.appendChild(resolutionWidget.element);

    const positionWidget = this.registerDisposer(new PositionWidget(
      this.navigationState.position, this.layerSpecification.coordinateSpaceCombiner, {label:true}));
    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      this.uiControlVisibility.showLocation, positionWidget.element));
    topRow.appendChild(positionWidget.element);

    const mousePositionWidget = this.registerDisposer(new MousePositionWidget(
      document.createElement('div'), this.mouseState, this.navigationState.coordinateSpace));
    mousePositionWidget.element.style.flex = '1';
    mousePositionWidget.element.style.alignSelf = 'center';
    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      this.uiControlVisibility.showLocation, mousePositionWidget.element));
    topRow.appendChild(mousePositionWidget.element);

    if (typeof NEUROGLANCER_CREDIT_LINK !== 'undefined') {
      let creditInfo = NEUROGLANCER_CREDIT_LINK!;
      if (!Array.isArray(creditInfo)) {
        creditInfo = [creditInfo];
      }
      for (const { url, text } of creditInfo) {
        const creditLink = document.createElement('a');
        creditLink.style.marginRight = '5px';
        creditLink.href = url;
        creditLink.textContent = text;
        creditLink.style.fontFamily = 'sans-serif';
        creditLink.style.color = 'yellow';
        creditLink.target = '_blank';
        topRow.appendChild(creditLink);
      }
    }

    const annotationToolStatus =
      this.registerDisposer(new AnnotationToolStatusWidget(this.selectedLayer, this.toolBinder));
    topRow.appendChild(annotationToolStatus.element);
    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      this.uiControlVisibility.showAnnotationToolStatus, annotationToolStatus.element));

    if (stateShareEnabled) {
      const stateShare = this.registerDisposer(new StateShare(this));
      topRow.appendChild(stateShare.element);
    }

    const centerTopRow = document.createElement('div');
    centerTopRow.classList.add('neuroglancer-viewer-center-top-row');

    const IFOperateRow = document.createElement('div');                          //  IF配准操作
    IFOperateRow.classList.add('neuroglancer-viewer-if-register-operate-row');
    const if_sign = makeElement('div',['neuroglancer-viewer-if-register-sign','neuroglancer-icon'],{},`${i18n.if_sign}`);
    const if_submit = makeElement('div',['neuroglancer-viewer-if-register-submit','neuroglancer-icon'],{},`${i18n.if_submit}`);
    IFOperateRow.appendChild(if_sign);
    IFOperateRow.appendChild(if_submit);
    if_sign.addEventListener('click',(_event)=>{
      new message('Hold Alt to sign.', 'info');
      new message('Right click to delete the marked point.', 'info');
      _event.stopPropagation();
      document.getElementsByClassName('neuroglancer-icon-IF-control')[0].setAttribute('data-biaoji','point');
    });
    if_submit.addEventListener('click',()=>{
      submitIF();
    })

    const IFRegisterRow = document.createElement('div');
    IFRegisterRow.classList.add('neuroglancer-viewer-if-register-row');  //  IF配准是展示 IF配准图像名称
    const if_IFName = document.createElement('div');
    const if_ssDNAName = document.createElement('div');
    if_IFName.classList.add('if-if-name');
    if_ssDNAName.classList.add('if-ssDNA-name');
    IFRegisterRow.appendChild(if_IFName);
    IFRegisterRow.appendChild(if_ssDNAName);

    const btnGroups = document.createElement('div')
    btnGroups.classList.add('btn-groups')

    const stepBtns = document.createElement('div')
    stepBtns.classList.add('neuroglancer-mulBtns','step-btns')
    // 所有button增加一个menu-bar包裹
    const menuBar = document.createElement('div')
    menuBar.classList.add('menu-bar');
    menuBar.style.display = 'flex';
    menuBar.style.alignItems = 'stretch';

    menuBar.classList.add('neuroglancer-mulBtns')
    btnGroups.appendChild(stepBtns)
    btnGroups.appendChild(menuBar)

    const bottomLeft = document.createElement('div');
    bottomLeft.classList.add('neuroglancer-viewer-bottom-left-row');
    gridContainer.appendChild(bottomLeft);

    const bottomRight = document.createElement('div');
    bottomRight.classList.add('neuroglancer-viewer-bottom-right-row');
    const scalebar = document.createElement('div');
    scalebar.classList.add('neuroglancer-viewer-scalebar');
    bottomRight.appendChild(scalebar)
    const legendContainer = document.createElement('div');
    legendContainer.classList.add('neuroglancer-viewer-legend-container');
    const legendView = document.createElement('div');
    legendView.classList.add('neuroglancer-viewer-legend-item');
    const legendTitle = document.createElement('div');
    legendTitle.classList.add('title','legend-image-cluster')
    legendTitle.innerHTML = i18n.legend_image;
    legendView.appendChild(legendTitle)
    legendContainer.appendChild(legendView)
    //  统计信息
    const statisticsInfoView = document.createElement('div');
    statisticsInfoView.classList.add('neuroglancer-viewer-parameter-item');
    statisticsInfoView.innerHTML = ``
    //  聚类参数
    const parameterView = document.createElement('div');
    parameterView.classList.add('neuroglancer-viewer-parameter-item');
    parameterView.innerHTML = `bin200 resolution=1`
    legendContainer.appendChild(parameterView)
    legendContainer.appendChild(statisticsInfoView)
    bottomRight.appendChild(legendContainer)
    gridContainer.appendChild(bottomRight);
    const logoContatin = document.createElement('div');
    const logo = new Image();
    logo.src = './static/logo.png';
    logoContatin.appendChild(logo);
    logoContatin.classList.add('neuroglancer-viewer-logo');
    // right-panel-area 虚拟区域
    createVirtualArea('right-panel-area', [80, 10, 0, 0], 280, 420)
    // physical-scale 虚拟区域
    createVirtualArea('physical-scale', [0, 0, 10, 10], 110, 30)
    this.layout = this.registerDisposer(new RootLayoutContainer(this, 'xy'));

    this.sidePanelManager = this.registerDisposer(
      new SidePanelManager(this.display, this.layout.element, this.visibility));
    // home返回上主页
    {
      const button = makeIcon({
        svg: svg_home,
        shape: 'circle',
        disabled: true,
        title: i18n.return_to_previous_page,
        classList: ['go-home'],
        throttleClick: () => {
          console.log('点击了--123')
        }
      });
      this.registerEventListener(button, 'click', () => {
        new SwitchV2Info(this, i18n.goHomeNotice,()=>{
          window.parent.postMessage({href: 'home'},'*');
        },()=>{})
      });
      // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //     this.uiControlVisibility.showEditStateButton, button));
      centerTopRow.appendChild(button);
    }

    // geneTable
    {
      const { genePanelState } = this;
      // const geneTabView = new GeneTabView(this.sidePanelManager, this.genePanelState, this.layerSpecification)
      const button =
        this.registerDisposer(new SelectView(genePanelState.location.watchableVisible, {
          // svg: svg_gene,
          // childElement: geneTabView.element,
          classes: ['gene-view'],
          defaultValue: (allToolsButton.genelistName as string).toLocaleLowerCase().includes('metabon') ? i18n.metabon : (allToolsButton.genelistName as string).toLocaleLowerCase().includes('protein') ? i18n.protein : i18n.gene,
          backgroundScheme: 'dark',
          enableTitle: i18n.genePanelShow,
          disableTitle: i18n.genePanelHide,
        }));
      button.element.id = 'gene-control-panel-button';
      this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
        this.uiControlVisibility.showGeneButton, button.element));
      // genetable
      this.registerDisposer(this.sidePanelManager.registerPanel({
        parent: button.element,
        location: this.genePanelState.location,
        isNeedAfterMakepanel: false,
        name: 'genetable',
        makePanel: () =>
        new newGeneTabView(this.sidePanelManager, this.genePanelState, this.layerSpecification, button.element),
      }));
      const {layersVisibleChanged} = this.layerManager
      layersVisibleChanged.add(() => {
        let disabled = true
        const {managedLayers} = this.layerManager
        for (let i = 0; i < managedLayers.length; i++) {
          if(managedLayers[i].name.indexOf('heatmap') > -1 && managedLayers[i].visible) {
            disabled = false;
            break;
          }
        }
        button.setDisable(disabled)
      })
      centerTopRow.appendChild(button.element);
    }

    // 图层选择
    {
      let classArr = ['layer-view'];
      // if(window.opener) classArr.push('lasso_disabled');
      const optionContainer = document.createElement('div')
      const button =
        this.registerDisposer(new SelectView(null, {
          // svg: svg_gene,
          classes: classArr,
          defaultValue: i18n.layer,
          backgroundScheme: 'dark',
          enableTitle: i18n.layerPanelShow,
          disableTitle: i18n.layerPanelHide,
          childElement: optionContainer
        }));
      const layerContainer = document.createElement('div')
      layerContainer.id = 'layer-container'
      optionContainer.appendChild(layerContainer)
      // optionContainer.appendChild(toolContainer)
      button.element.id = 'layer-control-panel-button';
      // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //   this.uiControlVisibility.showGeneButton, button.element));
      centerTopRow.appendChild(button.element);
    }
    // setting-begin
    const toolContainer = document.createElement('div')
    {
      toolContainer.classList.add('layer-tool-container')
      toolContainer.id = 'layer-tool-container'
      toolContainer.appendChild(this.registerDisposer(new ToolControl(this.showScaleBar, {
        label: i18n.scale_bar
      })).element)
      toolContainer.appendChild(this.registerDisposer(new ToolControl(this.showHoverInfo, {
        label: i18n.statistics,
      })).element)
      const legendTool= this.registerDisposer(new ToolControl(this.showLegend, {
        label: i18n.legend,
        onChange: () => {
          legendView.style.display = this.showLegend.value ? 'block' : 'none';
        }
      }));
      this.showLegend.changed.add(()=>{
        legendView.style.display = this.showLegend.value ? 'block' : 'none';
      })
      const {layersVisibleChanged} = this.layerManager
      toolContainer.appendChild(legendTool.element)
      layersVisibleChanged.add(() => {
        // const hasCluster = this.layerManager.getLayerVisible('cluster') || this.layerManager.getLayerVisible("proteinimage");
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP', 'proteinimage']);
        legendTool.setDisable(!hasCluster);
        this.showLegend.value = hasCluster;
        // legendView.style.display = hasCluster ? 'block':'none';
        this.updateScaleBarBinSize();
      })
      const statisticsInfoTool= this.registerDisposer(new ToolControl(this.showStatisticsInfo, {
        label: i18n.clustering_parameter,
        onChange: () => {
          statisticsInfoView.style.display = this.showStatisticsInfo.value ? 'block' : 'none';
        }
      }))
      layersVisibleChanged.add(debounce(() => {
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP']);
        const hasHeatmap = this.layerManager.getLayerVisible(['heatmap']);
        this.showStatisticsInfo.value = hasCluster || hasHeatmap;
        statisticsInfoTool.setDisable(!this.showStatisticsInfo.value);
        if(this.showStatisticsInfo.value){
          statisticsInfoView.classList.add('statisticsInfo');
          statisticsInfoView.style.width = 'auto';
          setTimeout(() => {
            if((hasCluster || hasHeatmap) && viewer.binSize.value === 'cellbin'){
              const cluster_layer = this.layerManager.managedLayers.filter((item:any) => (item.name.includes('cluster') || item.genefile === allToolsButton.genelistFile) && item.visible)[0];
              const cluster_file = cluster_layer?.genefile;
              const gene_or_protein = cluster_layer.genefile.toLocaleLowerCase().includes('metabon') ? i18n.metabon_type : cluster_layer.genefile.toLocaleLowerCase().includes('protein') ? i18n.protein_type : i18n.gene_type;
              statisticsInfoView.innerHTML = `<div style="margin: 0.75rem auto;justify-content: center">${i18n.clustering_parameter}</div>
              <div><span>${i18n.cell_count}:</span><span> ${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.cell_cell_count )}</span></div>
              <div><span>${i18n.cluster_count}:</span><span> ${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.cell_cluster_count )}</span></div>
              <div><span>${gene_or_protein}:</span><span> ${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.cell_gene_count )}</span></div>
              <div><span>${cluster_layer.genefile.toLocaleLowerCase().includes('metabon') ? i18n.intensity : i18n.mid_count}:</span><span> ${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.cell_mid_count )}</span></div>`
            }
          }, 500);
          if(hasHeatmap && viewer.binSize.value !== 'cellbin'){
            const heatmap_layerV = this.layerManager.managedLayers.filter((item:any) => item.heatmap && item.visible)[0];
            allToolsButton.genelistFile = heatmap_layerV.genefile;
            statisticsInfoView.innerHTML = `<div style="margin: 0.75rem auto;justify-content: center;">${i18n.clustering_parameter}</div>
            <div style="display: ${viewer.statisticsInfo[allToolsButton.genelistFile]?.gene_gef_area > 0 ? 'flex' : 'none'}"><span>${i18n.area}:</span> <span>${jishu( viewer.statisticsInfo[allToolsButton.genelistFile]?.gene_gef_area )} mm²</span></div>
            <div><span>${ heatmap_layerV.name.includes('metabon') ? i18n.metabon_type : heatmap_layerV.name.includes('protein') ? i18n.protein_type : i18n.gene_type}:</span><span>${thousandth_formatter( viewer.statisticsInfo[allToolsButton.genelistFile]?.gene_gene_count )}</span></div>
            <div><span>${ heatmap_layerV.name.includes('metabon') ? i18n.intensity : i18n.mid_count}:</span> <span>${thousandth_formatter( viewer.statisticsInfo[allToolsButton.genelistFile]?.gene_mid_count )}</span></div>`
          }
          if(hasCluster && viewer.binSize.value === h5ad_file_binsize){
            setTimeout(() => {
              const cluster_layer = this.layerManager.managedLayers.filter((item:any) => (item.name.includes('cluster') || item.name.includes('UMAP')) && item.visible)[0];
              const cluster_file = cluster_layer?.genefile;
              if(viewer.statisticsInfo[cluster_file]){
                const cluster_file = cluster_layer.genefile;
                const gene_or_protein = cluster_layer.genename.includes('metabon') ? i18n.metabon_type : cluster_layer.genename.includes('protein') ? i18n.protein_type : i18n.gene_type;
                statisticsInfoView.innerHTML = `<div style="margin: 0.75rem auto;justify-content: center;">${i18n.clustering_parameter}</div>
                <div><span>${i18n.bin_count}:</span> <span>${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.bin_count ? viewer.statisticsInfo[cluster_file]?.bin_count : 0)}</span></div>
                <div><span>${i18n.cluster_count}:</span><span>${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.cluster_count )}</span></div>
                <div><span>${gene_or_protein}:</span> <span>${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.gene_type ? viewer.statisticsInfo[cluster_file]?.gene_type : 0)}</span></div>
                <div><span>${cluster_layer.genename.includes('metabon') ? i18n.intensity : i18n.mid_count}:</span> <span>${thousandth_formatter( viewer.statisticsInfo[cluster_file]?.mid_count )}</span></div>`
              }
            }, 500);
          }
        }
        // if(hasCluster){
        //   isToolControl('add', ['tools-undo', 'measure_btn'])
        // }else{
        //   if (this.measureTool) {//测量状态下，不恢复配准按钮可点击状态
        //     isToolControl('remove', ['tools-undo', 'measure_btn', 'neuroglancer-icon-transform'])
        //   } else {
        //     isToolControl('remove', ['tools-undo', 'measure_btn'])
        //   }
        // }
      },500));
      toolContainer.appendChild(statisticsInfoTool.element);
      const parameterTool= this.registerDisposer(new ToolControl(this.showParameter, {
        label: i18n.resolution_parameter,
        onChange: () => {
          const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP']);
          if(hasCluster && viewer.binSize.value === 'bin200')parameterView.innerHTML = `bin200,resolution=0.5`
          parameterView.style.display = this.showParameter.value ? 'block' : 'none';
        }
      }))
      toolContainer.appendChild(parameterTool.element)
      layersVisibleChanged.add(debounce(() => {
        parameterView.classList.add('statisticsInfo');
        parameterView.style.width = 'auto';
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP']);
        this.showParameter.value = hasCluster && (viewer.binSize.value !== 'cellbin');
        parameterTool.setDisable(!this.showParameter.value);
        if(hasCluster && viewer.binSize.value !== 'cellbin' && viewer.showParameter.value){
          const file = viewer.layerManager.layerMap.get(allToolsButton.genelistName)?.genefile
          parameterView.innerHTML = `<div style="margin: 0.75rem auto;justify-content: center;">${i18n.resolution_parameter}</div>
          <span>${viewer.binSize.value} , ${i18n.resolution_parameter} ${viewer.resolution_parameter_value[file as string]}</span>`
        }
        this.updateScaleBarBinSize();
        // if(hasCluster){
        //   isToolControl('add', ['tools-undo', 'measure_btn'])
        // }
        // if ((allToolsButton.genelistName as string).toLocaleLowerCase().includes('metabon')) {
        //   rotateControl.element.classList.add('ng-common-disabled');
        //   rotateValControl.element.classList.add('ng-common-disabled');
        //   (rotateValControl.element.querySelector("input[type='range']") as any).disabled = true;
        // } else {
        //   rotateControl.element.classList.remove('ng-common-disabled');
        //   rotateValControl.element.classList.remove('ng-common-disabled');
        //   (rotateValControl.element.querySelector("input[type='range']") as any).disabled = false;
        // }
      }, 500))
      this.showStatisticsInfo.changed.add(()=>{
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP', 'heatmap']);
        statisticsInfoTool.setDisable(!(hasCluster || this.showStatisticsInfo.value));
      });
      this.showParameter.changed.add(()=>{
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP']);
        parameterTool.setDisable(!(hasCluster || this.showParameter.value));
      });
      this.binSize.changed.add(() => {
        const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP'])
        // if(hasCluster && viewer.binSize.value === 'bin200')parameterTool.setDisable(false)
        if(hasCluster && viewer.binSize.value === 'bin200'){
          this.showParameter.value = true;
          parameterTool.setDisable(false)
        }else{
          this.showParameter.value = false;
          parameterTool.setDisable(true)
        }
        this.updateScaleBarBinSize();
      })
      toolContainer.appendChild(this.registerDisposer(new ToolControl(this.showThumbnail, {
        label: i18n.thumbnail
      })).element);
      const rotate = new WatchableValue(0);
      const rotateControl = this.registerDisposer(new ToolControlInput (null, {
        label: i18n.rotate,
        onChange: (val: number) => {
          val = val || 0;
          rotate.value = val as number;
          this.rotateZ_axisTo(val);
          this.resetoreJson(JSON.parse(JSON.stringify(this.state.toJSON())));
        }
      }));
      (rotateControl.input as HTMLInputElement).valueAsNumber = this.getCurrentOrientationDeg();
      const rotateValControl = new RangeWidget(rotate, {max: 180,step: 1,name: 'rotate',min: -180}, true);
      rotateValControl.element.style.marginTop = '.75rem';
      rotate.changed.add(()=>{
        this.rotateZ_axisTo(rotate.value);
        (rotateControl.input as HTMLInputElement).value = Number(rotate.value).toFixed(0);
      })
      toolContainer.appendChild(rotateControl.element)
      toolContainer.appendChild(rotateValControl.element)
      //添加角度变化的监听，变化后给输入框修改角度值
      this.navigationState.pose.orientation.changed.add(()=>{
        let curAngle = this.getCurrentOrientationDeg();
        rotate.value = Number(curAngle.toString());
        rotateControl.setInputValue(Number(curAngle.toString()).toFixed(0));
      })
      // layersVisibleChanged.add(() => {
      //   const hasHeatmap = this.layerManager.getLayerVisible('heatmap')
      //   scalebar.style.display = this.showColorbar.value ? 'block' : 'none';
      //   this.showColorbar.value = hasHeatmap
      // })
    }
    // setting-end
    // binsize选择
    {
      // if(!window.opener) {
      if(!local_href_param.visible){
        let classArr = ['binsize-view'];
        const button =
          this.registerDisposer(new SelectView(null, {
            classes: classArr,
            defaultValue: this.binSize.value || allToolsButton.currentBinsize,
            backgroundScheme: 'dark',
            enableTitle: `${i18n.binsziePanelShow}`,
            disableTitle: `${i18n.binsziePanelHide}`,
            // childElement: getBinSizeList()
          }));
        button.element.id = 'gene-control-panel-button';
        button.element.addEventListener('click',()=>{
          if (button.element.classList.contains('lasso_disabled')) return
          button.element.getAttribute('data-checked') === 'true' ? button.element.appendChild(getBinSizeList()) : button.element.removeChild(document.getElementsByClassName('binsize-container')[0]!);
        });
        this.binSize.changed.add(() => {
          button.setLabel(this.binSize.value);
          const hasHeatmap = this.layerManager.getLayerVisible('heatmap')
          if(this.binSize.value === 'cellbin' && !hasHeatmap) scalebar.style.display = 'none';
        })
        this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
          this.uiControlVisibility.showGeneButton, button.element));
        centerTopRow.appendChild(button.element);
      }
    }
    // 设置
    {
      const button = makeIcon({
        svg: svg_settings,
        title: i18n.settings,
        classList: ['tools-setting', 'neuroglancer-selecteView', 'neuroglancer-icon'],
        checked: 'false',
        // disabled: true,
        throttleClick: () => {
          console.log('设置')
        },
        onClick: ()=>{
          let configDialog:any;
          if(button.getAttribute('data-checked') === 'false'){
            button.setAttribute('data-checked', 'true');
            const content:HTMLElement = document.createElement('div');
            const titile = makeElement('h4',[],{'style': 'text-align:center'},`${i18n.settings}`);
            titile.style.marginTop = '0';
            content.appendChild(titile);
            const optionContainer = document.createElement('div');
            content.appendChild(optionContainer);
            // const toolContainer = document.createElement('div')
            toolContainer.classList.add('layer-tool-container','neuroglancer-icon-settings')
            toolContainer.style.border = 'none';
            toolContainer.id = 'layer-tool-container'
            console.log(this.showStatisticsInfo)
            this.showStatisticsInfo.value
            optionContainer.appendChild(toolContainer);
            if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
              document.getElementsByClassName('tool-control-input-text')[0]?.setAttribute('readonly','true');
            }else{
              document.getElementsByClassName('tool-control-input-text')[0]?.removeAttribute('readonly');
            }
            configDialog = new ConfigIndependDialog(button,content, {position: 'bottom', unique: false, className: 'setting-configdialog'});
            configDialog?.buttonClose.addEventListener('click',()=>{
              button.setAttribute('data-checked', 'false');
            })
          }else{
            button.setAttribute('data-checked', 'false');
            let lassoBtn: any = document.getElementsByClassName('lassoBtn')[0];
            currentActivedTool = null
            let nodeList = document.body.querySelectorAll(".setting-configdialog") as NodeList;
            nodeList.forEach((item:Node)=>document.body.removeChild(item));
            if(lassoBtn?.dataset.checked === 'true') {
              lassoBtn.click();
            }
          }
        }
      });
      stepBtns.appendChild(button);
    }
    // 返回上一步
    {
      let arr = ['tools-undo', 'tools-control'];
      if (_param.visible) arr.push('disabled');
      const button = makeIcon({
        svg: svg_return,
        title: i18n.undo,
        classList: arr,
        // disabled: true,
        throttleClick: () => {
          console.log('undo')
        },
        onClick: async ()=>{
          if(viewer.jsonCloneObj.length > 1){
            viewer.jsonCloneObj.pop();
            const new_json = JSON.parse(JSON.stringify(viewer.jsonCloneObj[viewer.jsonCloneObj.length - 1]));
            viewer.bin200_self_newlabel_color = new_json.bin200_self_newlabel_color;
            viewer.self_newlabel_color = new_json.self_newlabel_color;
            if (dbStorageMoreWindow) {
              await dbStorageMoreWindow.update('layerName', {name: 'value', data: {bin200_self_newlabel_color: viewer.bin200_self_newlabel_color}})
            }
            localStorage.setItem('layerName', new Date().toLocaleString());
            if( Object.keys(viewer.saveSelectedKeyVal).length <= 0)viewer.proteinMergeColor.value = false;
            viewer.saveSelectedKeyVal = new_json.saveSelectedKeyVal ?? {};
            viewer.proteinMergeColor.value = new_json.proteinMergeColor;
            const heatmap_layer = new_json.layers.filter((item:any)=> item.heatmap)[0];
            if(heatmap_layer?.genecolor){
              const arr = colorArr.filter((item:any) => item.colorName === heatmap_layer.genecolor)[0];
              viewer.allDataSource.colorscale.colorData = formatterRGB1(arr.colormap, arr.reversed);
            }
            viewer.state.restoreState(new_json, false );
            const registerPanel:any = viewer.sidePanelManager.registeredPanels;
            for(let i of registerPanel){
              if(i?.name === 'genetable' && i.panel){
                i.panel?.initGeneTable();
                break;
              }else if(i?.name === 'genetable'){
                this.genePanelState.reset();
              }
            }
            if (this.curSegmentDisplayTab) {//初始化cluster tab，避免返回上一步操作后，cluster tab 多选框不同步、失效的问题
              this.curSegmentDisplayTab.dispose();
              setTimeout(()=>{
                this.initClusterSelectView();
              },0);
            }
            viewer.umapMamager.updateUmapView();
            viewer.configStorage.clear();
            // document.getElementsByClassName('tools-setting')[0].setAttribute('data-checked', 'false');
            if(new_json.showSingleGene?.value){
              const id = (viewer.showSingleGene.value as string).replace('cell_seg_','');
              const cluster = new_json.layers.filter((item:any)=> (item.name.includes('cluster') && item.visible) || ( item.name.includes('UMAP') && item.visible ) )[0].genefile
              const color = viewer.binSize.value === 'cellbin' ? viewer.self_newlabel_color[cluster][id] : viewer.bin200_self_newlabel_color[cluster][viewer.binSize.value as string][id];
              const cell = viewer.configStorage.restoreAllConfig.get(`cell_seg_${id}`);
              if(color){
                console.log(id, cell)
                const configDialog = new ConfigIndependDialog(cell, new CustomSegColorWidget(parseRGBColorSpecification(set16ToRgb(color, false)), id).element, {position: 'left', className: 'segColor-configdialog'});
                configDialog?.buttonClose.addEventListener('click',()=>{
                  viewer.configStorage.setActivedTool('')
                  viewer.clearActiveTool();
                })
              }
            }else{
              let nodeList = document.body.querySelectorAll(".segColor-configdialog") as NodeList;
              nodeList.forEach((item:Node)=>document.body.removeChild(item));
              new_json.showSingleGene = undefined;
            }
            if(new_json.selectedLayer?.layer && new_json.selectedLayer?.visible){
              const name = new_json.selectedLayer.layer;
              const cell = viewer.configStorage.restoreAllConfig.get(name);
              if (this.configStorage) this.configStorage.activedTool.value = 'abc';
              viewer.configStorage.clear();
              viewer.configStorage.registParentSingle(name, cell) // 每次点击选中都注册，避免restoState的时候dom更新，配置窗位置异常
              viewer.configStorage.setActivedTool(name);
            }else{
              viewer.configDialog?.close();
            }
          }
        }
      });
      // this.registerEventListener(button, 'click', () => {
        // this.editJsonState();
      // });
      // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //     this.uiControlVisibility.showEditStateButton, button));
      stepBtns.appendChild(button);
    }
    // 初始化状态
    {
      const button = makeIcon({
        svg: svg_reset,
        title: i18n.reset,
        classList: ['tools-reset'],
        // disabled: true,
        throttleClick: () => {
          new SwitchV2Info(this, i18n.resetNotice, async ()=>{
            // const path = location.href.replace('stereomapv3','stereomapv2')
            // window.location.href = path;
            let activeTool = currentActivedTool || currentActivedTool1
            if (activeTool) {
              activeTool.click()
              currentActivedTool = null
            }
            if(document.getElementsByClassName('neuroglancer-question')[0]){
              (document.getElementsByClassName('neuroglancer-question')[0] as HTMLElement).style.display = 'none';
              (document.getElementsByClassName('neuroglancer-icon-question')[0] as HTMLElement).dataset.checked = 'false';
            }
            viewer.bin200_self_newlabel = {};
            viewer.bin200_self_newlabel_color = {};
            viewer.self_newlabel = {};
            viewer.self_newlabel_color = {};
            viewer.saveSelectedKeyVal = {};
            viewer.showSingleGene.value = undefined;
            const setJson = JSON.parse(JSON.stringify(this.resetJson));
            setJson.selectedGenes = {};
            // viewer.color_scale.
            for(let k in viewer.color_scale){
              viewer.color_scale[k].isAutoScaleFlag = true;
            }
            // viewer.color_scale.isAutoScaleFlag = true;
            viewer.allDataSource.colorscale.genecolor = 'Default';
            viewer.allDataSource.colorscale.heatmapShader = `#uicontrol float sizeFactor slider(min=0.0, max=1.0, default=1.0, step=0.01); 
            #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
            void main() { 
              setColor(colormapNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));
              setPointMarkerFactor(sizeFactor);
              setPointMarkerOpacityFactor(oFactor - 0.01);
            }`;
            viewer.allDataSource.colorscale.colorData = [0.05,0.20,0.51, 0.00,0.37,0.64, 0.04,0.53,0.73,
              0.00,0.76,0.60,0.95,0.83,0.22,0.96,0.69,0.20,
              0.95,0.56,0.22,0.91,0.38,0.14,0.85,0.12,0.12,
              0.85,0.12,0.12]
            viewer.allDataSource.colorscale.colorStr = `vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))`;
            viewer.proteinsFilter.cellPositionHashSet?.clear();
            viewer.saveSelectedKeyVal = {};
            viewer.allDataSource.changeProteinColor = {};
            viewer.isBinsizeAuto.value = false;
            viewer.proteinMergeColor.value = false;
            viewer.binSize.value = allToolsButton.currentBinsize
            allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
            viewer.binSize.value = allToolsButton.currentBinsize;
            viewer.state.restoreState(setJson);
            viewer.genePanelState.reset();
            viewer.selectedLayer.layer = undefined;
            viewer?.configDialog?.close();
            viewer.configStorage.clear();
            viewer.configStorage.setActivedTool('');
            let nodeList = document.body.querySelectorAll(".selected-gene-more-dialog") as NodeList;
            nodeList.forEach((item:Node)=>document.body.removeChild(item));
            let details_nodeList = document.body.querySelectorAll(".more-details-configdialog") as NodeList;
            details_nodeList.forEach((item:Node)=>document.body.removeChild(item));
            let setting_nodeList = document.body.querySelectorAll(".setting-configdialog") as NodeList;
            if(setting_nodeList)setting_nodeList.forEach((item:Node)=>document.body.removeChild(item));
            const binsize_auto = document.getElementsByClassName('binsize-auto-control');
            if(binsize_auto && binsize_auto[0])binsize_auto[0].classList.remove('ng-common-disabled');
            this.jsonCloneObj = [];
            viewer.proteinMergeColor.value = false;
            this.rotateZ_axisTo(0);
            document.getElementsByClassName('tools-setting')[0]?.setAttribute('data-checked', 'false');
            document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]?.setAttribute('data-checked', 'false');
            disableStatus(false);
            if (dbStorageMoreWindow) {
              await dbStorageMoreWindow.update('layerName', {name: 'value', data: {href: viewer.binSize.value, value: [[],[]]}})
            }
            localStorage.setItem('layerName', new Date().toLocaleString());
            viewer.geoRegistrationTool?.dispose();
          }, () => {});
        }
      });
      // this.registerEventListener(button, 'click', () => {
      //   viewer.state.restoreState(resetJson);
      // });
      // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //     this.uiControlVisibility.showEditStateButton, button));
      stepBtns.appendChild(button);
    }
    // 鼠标游标
    if(userPemission.toolbar && userPemission.tool) {
      {
        const button = makeIcon({
          svg: svg_mouse_move,
          title: i18n.cursor
        });
        this.registerEventListener(button, 'click', () => {
          let activeTool = currentActivedTool || currentActivedTool1
          if (activeTool) {
            activeTool.click()
            currentActivedTool = null
          }
          document.getElementsByClassName('tools-setting')[0]?.setAttribute('data-checked', 'false');
          document.getElementsByClassName('tool-control-input-text')[0]?.removeAttribute('readonly');
          document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]?.setAttribute('data-checked', 'false');
          this.layerManager.managedLayers.forEach((item:any, index: number)=>{
            if(item.name ==='template'){
              this.layerManager.remove(index);
            }
          })
          document.getElementsByClassName('tools-setting')[0]?.setAttribute('data-checked', 'false');
          document.getElementsByClassName('tool-control-input-text')[0]?.removeAttribute('readonly');
          if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
            document.getElementsByClassName('neuroglancer-icon-transform')[0].setAttribute('data-checked','false');
            if(document.body.querySelector(".can_zoom_move_container")) document.body.removeChild((document.body.querySelector(".can_zoom_move_container") as HTMLDivElement));
            viewer.transformsize = 5;
          }
          if(viewer.layerCloneObj.layer.length){
            const json = viewer.state.toJSON();
            json.layers = viewer.layerCloneObj.layer;
            json.layers.forEach((item:any, index:number)=>{
              if(item.name === 'template'){
                json.layers.splice(index, 1)
              }
            });
            allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
            viewer.state.restoreState(json);
            viewer.layerCloneObj.layer = [];
          }
          let nodeList = document.body.querySelectorAll(".setting-configdialog") as NodeList;
          nodeList.forEach((item:Node)=>document.body.removeChild(item));
          let details_nodeList = document.body.querySelectorAll(".more-details-configdialog") as NodeList;
          details_nodeList.forEach((item:Node)=>document.body.removeChild(item));
          const hasCluster = this.layerManager.getLayerVisible(['cluster', 'UMAP', 'protein']);
          if(viewer.binSize.value !== 'cellbin' && !hasCluster){
            document.querySelectorAll('.tools-control').forEach((val)=>{
              const dom = (val as HTMLElement);
              if(!viewer.allToolsControlDisable.includes(dom)) {
                dom.classList.remove('disabled')
              }
              dom.dataset.checked = 'false';
              const dom_message = document.getElementsByClassName('message-box-close')[0] as HTMLElement;
              if(dom_message)removeFromParent(dom_message)
            });
          }
          isSelectView(true, 'layer-view');
          disableStatus(false);
          if (this.measureTool) {
            this.measureTool.dispose();
            this.measureTool = undefined;
          }
          viewer.geoRegistrationTool?.dispose();
        });
        // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
        //     this.uiControlVisibility.showEditStateButton, button));
        menuBar.appendChild(button);
      }
    }
      // 手动配准
      if (userPemission.toolbar && !local_href_param.isReadOnly) {
        {
          const button = makeElement('div', ['neuroglancer-icon', 'neuroglancer-checkbox-icon', 'dark-background', 'neuroglancer-icon-transform', 'tools-control', `${!allToolsButton.isTransform ? 'disabled' : 'enable'}`], {
            'data-checked': 'false',
            'title': i18n.registration_with_track_line,
            'isssDNA': 'true'
          }, svg_peizhun)
          this.pemission.register(button, 'register')
          let configDialog: any
          !allToolsButton.isTransform ? this.allToolsControlDisable.push('neuroglancer-icon-transform', button):'';
          button.addEventListener('click', async () => {
            let _json: any;
            let _layers: any;
            _json = viewer.state.toJSON();
            if (button.getAttribute('data-checked') === 'false') {
              if (!(await check_balance())) return
              viewer.configStorage.setActivedTool('')
              document.getElementsByClassName('tools-setting')[0]?.setAttribute('data-checked', 'false');
              let nodeList = document.body.querySelectorAll(".setting-configdialog") as NodeList;
              nodeList.forEach((item:Node)=>document.body.removeChild(item));
              let gene_nodeList = document.body.querySelectorAll(".selected-gene-more-dialog") as NodeList;
              gene_nodeList.forEach((item:Node)=>document.body.removeChild(item));
              let details_nodeList = document.body.querySelectorAll(".more-details-configdialog") as NodeList;
              details_nodeList.forEach((item:Node)=>document.body.removeChild(item));
              this.rotateZ_axisTo(0);
              disableStatus(true);
              _json = viewer.state.toJSON();
              _layers = viewer.state.toJSON().layers;
              viewer.layerCloneObj = JSON.parse(JSON.stringify(_json.layers));
              const gene_list_file = allToolsButton.genelistFile;
              viewer.layerCloneObj = {
                layer: JSON.parse(JSON.stringify(_json.layers)),
                layout: JSON.parse(JSON.stringify(_json.layout)),
                gene_list_file: gene_list_file
              }
              if (typeof(_json.layout) === 'object') {
                viewer.layerCloneObj.layer.map((layer:any) => {
                  if (layer.name.includes('UMAP') || layer.name.includes('cluster')) {
                    layer.visible = true
                  }
                  return layer
                })
              }
              beforeActiveTool()
              document.getElementsByClassName('tool-control-input-text')[0]?.setAttribute('readonly','true');
              viewer.layerManager.managedLayers.forEach((item)=>{
                if ( (item.name.indexOf('ssDNA') < 0 || item.name.indexOf('DAPI') ) && item.type !== 'annotation') {
                  item.setDisabled(true);
                }
              });
              button.dataset.name = 'registration_with_track_line'
              currentActivedTool = button;
              for (let j = 0; j < _layers.length; j++) {
                if ((_layers[j].heatmap && _layers[j].genename === allToolsButton.genelistFile) || _layers[j].name === 'template') {
                  _layers[j].hasData = true;
                  _layers[j].visible = true;
                  _layers[j].shaderControls = {
                    'oFactor': 0.5
                  }
                  _layers[j].opacity = 0.5
                } else {
                  _layers[j].visible = false;
                  _layers[j].disabled = true;
                }
              }
              const firstKey = allToolsButton.isTransform[0][0];
              const firstVal = allToolsButton.isTransform[0][1];
              const shader = allToolsButton.isTransform[0][2] > 1 ? `#uicontrol vec3 color color(default="#E26EF2")
          #uicontrol float brightness slider(min=-1, max=1)
          #uicontrol float contrast slider(min=-3, max=3, step=0.01)
          void main() {
            emitRGB(
              vec3( (toNormalized(getDataValue(0)) + brightness ) * exp(contrast),
                    (toNormalized(getDataValue(1)) + brightness ) * exp(contrast),
                    (toNormalized(getDataValue(2)) + brightness ) * exp(contrast)));
          }` : `#uicontrol invlerp normalized\n    void main() {\n      emitGrayscale(normalized());\n    }`
              _layers.push({
                hasData: true,
                visible: true,
                source: `precomputed://${local_host_SN_flag}/image/transform/${firstVal}`,
                // source: `precomputed://${local_host_SN_flag}/image/${firstVal}`,
                genetype: 'transform',
                name: `transform_${firstKey}`,
                genename: `transform_${firstKey}`,
                shader: shader,
                tab: 'rendering',
                type: 'image',
                opacity: 1,
              })
              _json.layers = _layers;
              button.dataset.checked = 'true';
              viewer.state.restoreState(_json);
              viewer.selectedLayer.layer = viewer.layerManager.managedLayers.filter((item: any) => item.name === `transform_${firstKey}`)[0];
              console.log(viewer.selectedLayer.layer.layer)
              configDialog = new ConfigIndependDialog(
                button,
                new RenderingOptionsTab(viewer.selectedLayer.layer.layer as ImageUserLayer).transformUI, {
                  position: 'bottom',
                  unique: false
                });
              console.log((viewer.selectedLayer.layer?.layer as ImageUserLayer).dataSources[0].loadState)
              configDialog?.buttonClose.addEventListener('click', () => {
                deleteSignpoint();
                button.setAttribute('data-checked', 'false');
                document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]?.setAttribute('data-checked', 'false');
                document.getElementsByClassName('tool-control-input-text')[0]?.removeAttribute('readonly');
                let nodeList = document.body.querySelectorAll(".can_zoom_move_container") as NodeList;
                nodeList.forEach((item: Node) => document.body.removeChild(item));
                viewer.configStorage.setActivedTool('')
                viewer.layerManager.managedLayers.forEach((item: any, index: number) => {
                  item.setDisabled(false);
                  if (item.name === 'template') {
                    viewer.layerManager.managedLayers.splice(index, 1)
                  }
                });
                currentActivedTool = null;
                viewer.configStorage?.clear()
                let json = viewer.state.toJSON();
                json.layout = viewer.layerCloneObj.layout;
                json.layers = JSON.parse(JSON.stringify(viewer.layerCloneObj.layer));
                json.layers.forEach((item: any, index: number) => {
                  if (item.name === 'template') {
                    json.layers.splice(index, 1)
                  }
                })
                allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
                viewer.state.restoreState(json);
                viewer.layerCloneObj = {
                  layer: [],
                  layout: 'xy',
                  gene_list_file: allToolsButton.genelistFile
                };
                isToolControl('remove', ['neuroglancer-icon-transform']);
                isSelectView(true, ['layer-view', 'tools-setting']);
                let lassoBtn: any = document.getElementsByClassName('lassoBtn')[0];
                if (lassoBtn?.dataset.checked === 'true') {
                  lassoBtn.click();
                }
                viewer.imageSourceModelMat4 = mat4.create();
                viewer.imageSourceModelMat4Inverse = mat4.create();
                disableStatus(false);
              });
              isToolControl('add', ['neuroglancer-icon-transform', 'neuroglancer-icon-standardAnalysis']);
              isSelectView(false, ['tools-setting', 'layer-view']);
            } else {
              document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]?.setAttribute('data-checked', 'false');
              deleteSignpoint();
              isToolControl('remove', ['neuroglancer-icon-transform', 'neuroglancer-icon-standardAnalysis']);
              disableStatus(false);
              currentActivedTool = null
              document.getElementsByClassName('tool-control-input-text')[0]?.removeAttribute('readonly');
              let nodeList = document.body.querySelectorAll(".can_zoom_move_container") as NodeList;
              if (nodeList) nodeList.forEach((item: Node) => document.body.removeChild(item));
              button.dataset.checked = 'false';
              let json = viewer.state.toJSON();
              json.layers = JSON.parse(JSON.stringify(viewer.layerCloneObj.layer));
              json.layers.forEach((item: any, index: number) => {
                if (item.name === 'template') {
                  json.layers.splice(index, 1)
                }
              })
              json.layout = viewer.layerCloneObj.layout;
              allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
              viewer.state.restoreState(json);
              isSelectView(true, ['layer-view', 'tools-setting']);
              let lassoBtn: any = document.getElementsByClassName('lassoBtn')[0];
              if (lassoBtn?.dataset.checked === 'true') {
                lassoBtn.click();
              }
              viewer.configStorage?.clear()
            }
          });
          button.setAttribute('id', 'manual-registration-button');
          const {layersVisibleChanged} = this.layerManager
          layersVisibleChanged.add(() => {
            if (!this.measureTool) {//测量状态下，不恢复配准按钮可点击状态
              const hasHeatmap = this.layerManager.getLayerVisible('heatmap');
              console.log(hasHeatmap, allToolsButton.isTransform, !viewer.proteinMergeColor.value)
              button.classList[hasHeatmap && allToolsButton.isTransform && !viewer.proteinMergeColor.value && viewer.binSize.value !== 'cellbin' ? 'remove' : 'add']("disabled");
              // button.classList[hasHeatmap && allToolsButton.isTransform && viewer.binSize.value !== 'cellbin' ? 'remove' : 'add']("disabled");
              if(!(allToolsButton.omics as string).includes('Transcriptomics')) {
                button.classList.add('disabled');
              }
            }
          });
          button.setAttribute('id', 'manual-registration-button');
          menuBar.appendChild(button);
        }
      }
      // lasuo
if (userPemission.toolbar && !local_href_param.isReadOnly) {
        {
          const button = makeElement('div', ['neuroglancer-icon', 'lassoBtn', 'tools-control'], {
            'data-checked': 'false',
            'title': i18n.lasso
          }, svg_lasso);
          this.pemission.register(button, 'lasso')
          this.registerEventListener(button, 'click', async () => {
            if( !(button.getAttribute('data-checked') === 'true') ){
              if(!(await check_balance()))return
              new Lasso(this, button);
            } else{
              viewer.lassoTool?.dispose();
            }
          });
          menuBar.appendChild(button);
          const {layersVisibleChanged} = this.layerManager;
          layersVisibleChanged.add(() => {
            const hasCluster = this.layerManager.getLayerVisible(['cluster']);
            const hasHeatmap = this.layerManager.getLayerVisible(['heatmap']);
            button.classList[((hasCluster && viewer.binSize.value === 'cellbin') || (hasHeatmap && !viewer.proteinMergeColor.value && viewer.binSize.value !== 'cellbin')) ? 'remove' : 'add']("disabled");
            const transform_checked = document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true';
            const measure_checked = document.getElementsByClassName('measure_btn')[0] && document.getElementsByClassName('measure_btn')[0].getAttribute('data-checked') === 'true';
            if (transform_checked || measure_checked || viewer.geoRegistrationTool) button.classList.add('disabled');
          })
          // this.binSize.changed.add(() => {
          //   const hasCluster = this.layerManager.getLayerVisible(['cluster']);
          //   const hasHeatmap = this.layerManager.getLayerVisible(['heatmap']);
          //   button.classList[( (hasCluster && viewer.binSize.value === 'cellbin') || (hasHeatmap && !viewer.proteinMergeColor.value) )? 'remove': 'add']("disabled");
          //     const transform_checked = document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true';
          //     const measure_checked = document.getElementsByClassName('measure_btn')[0] && document.getElementsByClassName('measure_btn')[0].getAttribute('data-checked') === 'true';
          //     if(transform_checked || measure_checked) button.classList.add('disabled');
          // })
        }
      }
      // filter
      // {
      //   const button = makeIcon({
      //     svg: svg_filter,
      //     title: i18n.filter,
      //     disabled: true
      //   });
      //   this.registerEventListener(button, 'click', () => {

      //     // this.editJsonState();
      //   });
      //   // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //   //     this.uiControlVisibility.showEditStateButton, button));
      //   menuBar.appendChild(button);
      // }
      // 批注
      // {
      //   const button = makeIcon({
      //     svg: svg_pizhu,
      //     title: i18n.comments,
      //     disabled: true
      //   });
      //   this.registerEventListener(button, 'click', () => {

      //     // this.editJsonState();
      //   });
      //   // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      //   //     this.uiControlVisibility.showEditStateButton, button));
      //   menuBar.appendChild(button);
      // }
      // 配准模板
      if (userPemission.toolbar && userPemission.tool && !local_href_param.isReadOnly) {
        {
          const button = makeIcon({
            svg: svg_peizhunmoban,
            title: i18n.registration_template,
            disabled: !allToolsButton.isStandardAnalysis
          });
          button.classList.add('tools-control', 'neuroglancer-icon-standardAnalysis');
          button.dataset.checked = 'false';
          !allToolsButton.isStandardAnalysis ? this.allToolsControlDisable.push('neuroglancer-icon-standardAnalysis', button) : '';
          this.registerEventListener(button, 'click', () => {
            this.configStorage.clear();
            this.configDialog?.close();
            if (button.getAttribute('data-checked') === 'false') {
              disableStatus(true, 'template');
              let _layers = viewer.state.toJSON().layers;
              const templateLayer = [{
                "source": `precomputed://${local_host_SN_flag}/annotation/ipr/bin10`,
                "tab": "rendering",
                "type": "annotation",
                "shader": `#uicontrol float sizeFactor slider(min=0.0, max=4.0, default=1.0, step=0.01); 
            #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
            void main() { 
              setColor(defaultColor());
              setPointMarkerFactor(sizeFactor);
              setPointMarkerOpacityFactor(oFactor - 0.01);
            }`,
                "genename": 'template',
                "name": 'template',
                "hasData": true,
                "visible": true
              }]
              _layers.push(templateLayer);
              this.layerSpecification.restoreSingleLayer(templateLayer)
              button.dataset.checked = 'true';
            } else {
              disableStatus(false, 'template');
              button.dataset.checked = 'false';
              this.layerManager.managedLayers.forEach((item: any, index: number) => {
                if (item.name === 'template') {
                  this.layerManager.remove(index);
                }
              })
              if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'false'){
                isSelectView(true, 'layer-view');
              }else{
                disableStatus(true);
              }
            }
          });
          const {layersVisibleChanged} = this.layerManager
          layersVisibleChanged.add(() => {
            const hasHeatmap = this.layerManager.getLayerVisible(['UMAP', 'cluster']);
            button.classList[!hasHeatmap && allToolsButton.isStandardAnalysis && !viewer.proteinMergeColor.value ? 'remove' : 'add']("disabled");
            if ((hasHeatmap && allToolsButton.isStandardAnalysis)) {
              button.dataset.checked = 'false';
              this.layerManager.managedLayers.forEach((item: any, index: number) => {
                if (item.name === 'template') {
                  this.layerManager.remove(index);
                }
              });
              disableStatus(false);
            }
            if (this.layerManager.getLayerVisible('UMAP') && allToolsButton.isStandardAnalysis) {
              button.dataset.checked = 'false';
            }
          });
          // layersVisibleChanged.add(()=>{
          //   const hasHeatmap = this.layerManager.getLayerVisible(['UMAP', 'cluster']);
          //   button.classList[hasHeatmap && allToolsButton.isStandardAnalysis && !viewer.proteinMergeColor.value ? 'add' : 'remove']("disabled");
          // });
          menuBar.appendChild(button);
        }
      }
      // 测量
      if (_param.DataID) {
        {
          const button = makeIcon({
            svg: svg_measure,
            title: i18n.measure,
            disabled: false

          });
          button.classList.add('tools-control', 'measure_btn');
          button.dataset.checked = 'false';
          this.registerEventListener(button, 'click', () => {
            if (!this.measureTool) {
              const mainViewPanel = this.display.orderedPanels.find((item) => {
                return item instanceof SliceViewPanel && item.sliceView.panelType !== 'UMAP';
              });
              if (mainViewPanel) {
                this.measureTool = new MeasureTool(this, mainViewPanel as SliceViewPanel);
                button.dataset.checked = 'true';
              }
            } else {
              this.measureTool.dispose();
              button.dataset.checked = 'false';
              this.measureTool = undefined;
            }
          });
          menuBar.appendChild(button);
        }
      }
    // source panel-暂时屏蔽
  //   {
  //     const { selectedLayer } = this;
  //     const button = this.registerDisposer(new CheckboxIcon(
  //       {
  //         get value() {
  //           return selectedLayer.visible;
  //         },
  //         set value(visible: boolean) {
  //           selectedLayer.visible = visible;
  //         },
  //         changed: selectedLayer.location.locationChanged,
  //       },
  //       {
  //         svg: svg_controls_alt,
  //         backgroundScheme: 'dark',
  //         enableTitle: 'Show layer side panel',
  //         disableTitle: 'Hide layer side panel'
  //       }));
  //     this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
  //       this.uiControlVisibility.showLayerSidePanelButton, button.element));
  //     button.element.id = 'layer-side-panel-button';
  //     menuBar.appendChild(button.element);
  // }

    // selectDetail panel
    // {
    //   const {selectionDetailsState} = this;
    //   const button =
    //       this.registerDisposer(new CheckboxIcon(selectionDetailsState.location.watchableVisible, {
    //         svg: svg_list,
    //         backgroundScheme: 'dark',
    //         enableTitle: 'Show selection details panel',
    //         disableTitle: 'Hide selection details panel'
    //       }));
    //   this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
    //       this.uiControlVisibility.showSelectionPanelButton, button.element));
    //   topRow.appendChild(button.element);
    // }

    // show edit layer
    // {
    //   const button = makeIcon({text: '{}', title: 'Edit JSON state'});
    //   this.registerEventListener(button, 'click', () => {
    //     this.editJsonState();
    //   });
    //   this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
    //       this.uiControlVisibility.showEditStateButton, button));
    //   topRow.appendChild(button);
    // }

    // add question
    // {
    //   // const button = makeIcon({svg: })
    //   const button = makeElement('div', ['neuroglancer-icon', 'neuroglancer-checkbox-icon', 'dark-background','neuroglancer-icon-question'], { 'data-checked': 'false', 'title': 'Questionnaire' }, `<svg t="1643277770131" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2476" width="16" height="16"><path d="M895.946667 734.048l1.066666 1.013333a29.824 29.824 0 0 1 0 43.413334l-162.261333 152.96a31.925333 31.925333 0 0 1-22.762667 8.704 31.925333 31.925333 0 0 1-22.773333-8.704l-93.184-87.84a29.824 29.824 0 0 1 0-43.413334l1.077333-1.013333a32 32 0 0 1 43.904 0l70.976 66.901333 140.053334-132.021333a32 32 0 0 1 43.904 0zM768 85.333333c64.8 0 117.333333 52.533333 117.333333 117.333334v394.666666a32 32 0 0 1-64 0V202.666667a53.333333 53.333333 0 0 0-53.333333-53.333334H256a53.333333 53.333333 0 0 0-53.333333 53.333334v618.666666a53.333333 53.333333 0 0 0 53.333333 53.333334h234.666667a32 32 0 0 1 0 64H256c-64.8 0-117.333333-52.533333-117.333333-117.333334V202.666667c0-64.8 52.533333-117.333333 117.333333-117.333334zM554.666667 544a32 32 0 0 1 0 64H341.333333a32 32 0 0 1 0-64z m128-170.666667a32 32 0 0 1 0 64H341.333333a32 32 0 0 1 0-64z" p-id="2477" fill="#ffffff"></path></svg>`);
    //   // const code = makeElement('div', ['neuroglancer-question'], { 'style': 'position: fixed;display: none;top: 50%; left: 50%;transform: translate(-50%, -50%);width:300px;z-index: 10' }, `<p class="question-close" style="color: #fff">X</p><img style="width: 100%" src="static/request_code.png" />`)
    //   button.addEventListener('click', () => {
    //     window.open('https://www.wjx.cn/vm/PTyao3v.aspx#');
    //     // button.setAttribute('data-checked', button.getAttribute('data-checked') === 'false' ? 'true' : 'false');
    //     // code.style.display = button.getAttribute('data-checked') === 'false' ? 'none' : 'block';
    //     // button.setAttribute('title', button.getAttribute('data-checked') === 'false' ? 'Show questionnaire' : 'Hide questionnaire');
    //   })
    //   // document.body.appendChild(code)
    //   button.setAttribute('id', 'questionnaire-button')
    //   topRow.appendChild(button);
    //   // code.getElementsByClassName('question-close')[0].addEventListener('click', () => {
    //   //   code.style.display = 'none';
    //   //   button.setAttribute('data-checked', 'false');
    //   // })
    // }

    // 跳转v2
    // {
    //   const button = makeElement('div',['neuroglancer-icon', 'neuroglancer-checkbox-icon', 'dark-background','go-v3'],{},`<svg t="1667456646624" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2956" width="10" height="10"><path d="M99.119848 428.002088h828.014506a43.350032 43.350032 0 0 0 43.350032-43.350031 43.248882 43.248882 0 0 0-16.451337-33.957525 43.465632 43.465632 0 0 0-9.219107-8.756707L635.50424 124.869766a43.350032 43.350032 0 1 0-49.809186 70.964002l207.270952 145.461032H99.119848a43.350032 43.350032 0 0 0 0 86.707288zM927.134354 601.07709H99.119848a43.350032 43.350032 0 0 0-43.350032 43.350032 43.205532 43.205532 0 0 0 16.451337 33.9503c2.564877 3.272927 5.635504 6.242405 9.219107 8.756706l309.309701 217.075284a43.313907 43.313907 0 0 0 60.386594-10.584632 43.350032 43.350032 0 0 0-10.577407-60.386595L233.288196 687.777154h693.846158a43.350032 43.350032 0 0 0 0-86.700064z" fill="#6646be" p-id="2957"></path></svg><span>Switch to V2</span>`)
    //   button.addEventListener('click',()=>{
    //     new SwitchV2Info(this, `The operations performed on this page won't be retained. Are you sure to switch?`, ()=>{
    //       const path = location.href.replace('stereomapv3','stereomapv2')
    //       window.location.href = path;});
    //   })
    //   topRow.appendChild(button)
    // }

    // add total protein control
    // {
    //   const { proteinPanelState } = this;
    //   const button =
    //     this.registerDisposer(new CheckboxIcon(proteinPanelState.location.watchableVisible, {
    //       svg: svg_controls,
    //       backgroundScheme: 'dark',
    //       enableTitle: 'Show protein panel',
    //       disableTitle: 'Hide protein panel'
    //     }));
    //   button.element.classList.add('protein-control-panel-button');
    //   // button.element.style.display = 'none';
    //   this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
    //     this.uiControlVisibility.showProteinButton, button.element));
    //   menuBar.appendChild(button.element);
    // }

    centerTopRow.appendChild(btnGroups);

    // 聚类
    {
        const button = this.cluterView =
          this.registerDisposer(new SelectView(null, {
            // svg: svg_gene,
            classes: ['cluster-view'],
            defaultValue: i18n.clustering,
            backgroundScheme: 'dark',
          enableTitle: i18n.clusterPanelShow,
          disableTitle: i18n.clusterPanelHide,
            onClick: (val:boolean) => {
              if (val && button.element?.children.length < 2) {
                this.initClusterSelectView();
              }
            }
          }));
        button.element.id = 'cluster-button';
        const {layersVisibleChanged} = this.layerManager
        layersVisibleChanged.add(() => {
          button.setDisable(!this.layerManager.getLayerVisible(['cluster', 'UMAP']))
        })
        // 初始化状态
        centerTopRow.appendChild(button.element);
    }

    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
      makeDerivedWatchableValue(
        (...values: boolean[]) => values.reduce((a, b) => a || b, false),
        ...VIEWER_TOP_ROW_CONFIG_OPTIONS.map(key => this.uiControlVisibility[key])),
      topRow));

    gridContainer.appendChild(topRow);
    gridContainer.appendChild(centerTopRow);
    gridContainer.appendChild(IFOperateRow)
    gridContainer.appendChild(IFRegisterRow);
    gridContainer.appendChild(logoContatin);
    const centerLeft = document.createElement('div');

    centerLeft.classList.add('neuroglancer-viewer-left-row');
    centerLeft.style.display = 'flex';
    centerLeft.style.flexDirection = 'column';
    centerLeft.style.alignItems = 'stretch';
    gridContainer.appendChild(centerLeft);

    const scaleContainer = document.createElement('div');
    scaleContainer.classList.add('scale-container')
    centerLeft.appendChild(scaleContainer)

    const scaleLarge = new Image();
    scaleLarge.src = './static/fangda.png';
    scaleLarge.className = 'scale-large';
    scaleLarge.onclick = () => {
      scaleControl.stepChange('up');
    };
    const scaleMinus = new Image();
    scaleMinus.src = './static/suoxiao.png';
    scaleMinus.className = 'scale-minus';
    scaleMinus.onclick = () => {
      scaleControl.stepChange('down');
    };
    scaleContainer.appendChild(scaleLarge);
    scaleContainer.appendChild(scaleMinus);
    const scaleNum = document.createElement('span');
    scaleContainer.appendChild(scaleNum);

    const scaleMax = 10;
    SliceViewPanel.scaleMin = 1 / scaleMax;
    const scale = this.scale;
    const scaleControl = new RangeWidget(scale, {
      max: scaleMax,
      step: 0.001,
      name: 'scale'
    }, true);
    scaleContainer.appendChild(scaleControl.element);
    scaleContainer.appendChild(document.createElement('div'))
    scale.changed.add(()=>{
      this.crossSectionScale.value = 1 / scale.value;
      scaleNum.innerHTML = (scale.value * 100).toFixed(1) + '%';
    });
    let time = 0
    this.isBinsizeAuto.changed.add(()=>{
      if(this.isBinsizeAuto.value){
        time = 1000;
        document.getElementsByClassName('binsize-view')[0]?.classList.add('is-binsize-auto');
        const bin = this.binSize.value?.replace('bin','');
        let targetBinSize = 'bin50';
        if(Number(bin) >= 100){
          targetBinSize = 'bin50';
        }
        const layer = viewer.layerManager.managedLayers.filter((item:any)=>item.heatmap);
        layer[0].setVisible(true);
        if(scale.value >= 1){
          targetBinSize = allToolsButton.bin_list[0]; // bin1
        }else if(scale.value >= 0.4 && scale.value < 1){
          targetBinSize = allToolsButton.bin_list.includes('bin10') ? 'bin10' : allToolsButton.bin_list[0];
        }else if(scale.value >= 0.15 && scale.value < 0.4){
          if(allToolsButton.bin_list.includes('bin20')){
            targetBinSize = 'bin20';
          }else{
            targetBinSize = allToolsButton.bin_list[1];
          }
        }else if(scale.value >= 0.0 && scale.value < 0.15){
          if(allToolsButton.bin_list.includes('bin50')){
            targetBinSize = 'bin50';
          }else{
            targetBinSize = allToolsButton.bin_list[2];
          }
        }
        if(viewer.binSize.value !== targetBinSize) changeBinSize(targetBinSize);
      }else{
        document.getElementsByClassName('binsize-view')[0]?.classList.remove('is-binsize-auto');
      }
    });
    this.binSize.changed.add(debounce(()=>{
      if(document.getElementsByClassName('binsize-view')[0]){
        (document.getElementsByClassName('binsize-view')[0].getElementsByClassName('selecte-text')[0]).innerHTML = this.binSize.value as string;
      }
      /*if(this.isBinsizeAuto.value){
        changeBinSize(this.binSize.value as string)
      }*/
    },1000))
    this.binSize.changed.add(()=>{
      viewer.updateScaleBarBinSize();//更新scaleBar的binSize
      document.getElementsByClassName('binsize-auto-control')[0]?.classList[viewer.binSize.value === 'cellbin' ? 'add': 'remove']('ng-common-disabled');
    })
    this.crossSectionScale.changed.add(debounce(()=>{
      // if (!this.initScale && Number.isFinite(this.crossSectionScale.value)) {
      //   this.initScale = this.crossSectionScale.value
      //   viewer.initStateJson = viewer.state.toJSON()
      // }
      // 加个延时，让scale.value的计算在restoreMin之后
      let scale_unit = [100, 40, 15, 0];
      setTimeout(() => {
        let num = !isNaN(1 / this.crossSectionScale.value) ? (1 / this.crossSectionScale.value) : 0;
        let minScale = this.homeCrossSectionScale * this.minScaleFactor;
        scale.value = num > 10 ? 10 : num < minScale ? minScale : num;
        scaleNum.innerHTML = (scale.value * 100).toFixed(1) + '%';
        if(this.isBinsizeAuto.value){
          if(document.getElementById('gene-control-panel-button')?.classList.contains('lasso_disabled')){
            viewer.isBinsizeAuto.value = false;
            return;
          }
          const lastIndex = (allToolsButton.bin_list as [string]).lastIndexOf('bin100');
          if(lastIndex === 4)scale_unit = [100, 40, 15, 0];
          if(lastIndex === 3)scale_unit = [100, 15, 15, 0];
          let targetBinSize;
          if(scale.value * 100 >= scale_unit[0]){
            targetBinSize = allToolsButton.bin_list[0]   // bin1
          }else if(scale.value * 100 >= scale_unit[1] && scale.value * 100 <= scale_unit[0]){
            targetBinSize = allToolsButton.bin_list.includes('bin10') ? 'bin10' :  allToolsButton.bin_list[1] // bin10
          }else if(scale.value * 100 >= scale_unit[2] && scale.value * 100 <= scale_unit[1]){
            targetBinSize = lastIndex === 3 ? allToolsButton.bin_list[1] : allToolsButton.bin_list.includes('bin20') ? 'bin20' : allToolsButton.bin_list[2]  // bin20
          }else{
            targetBinSize = lastIndex === 3 ? allToolsButton.bin_list[2] : allToolsButton.bin_list.includes('bin50') ? 'bin50' : allToolsButton.bin_list[3]  // bin50
          }
          if(viewer.binSize.value !== targetBinSize) changeBinSize(targetBinSize);
        }
      }, 0);
    }, time))
    const navigatorWidget = this.registerDisposer(new CoronalPlaneNavigatorWidget(this.display, this.navigationState));
    gridContainer.appendChild(navigatorWidget.element);

    this.registerDisposer(this.showThumbnail.changed.add(()=>{
      navigatorWidget.setEnable(this.showThumbnail.value);
    }));

    const setScaleRangeFn = ()=>{
      let boundSize = this.coordinateSpace.boundSize;
      let rect = this.display.canvasRect;
      let wrapWidth = rect!.width;
      let wrapHeight = rect!.height;
      let x: any = undefined, y: any = undefined;
      if(typeof boundSize[0] === 'number' && !isNaN(boundSize[0]) && boundSize[0] !== Infinity ) {
        if(x == boundSize[0] && y == boundSize[1]) return;
        x = boundSize[0];
        y = boundSize[1];
        this.homeCrossSectionScale = Number((1 / ((x / y) > (wrapWidth / wrapHeight) ? x / wrapWidth : y / wrapHeight)));
        // if (!viewer.initStateJson)  viewer.initStateJson = viewer.state.toJSON()
        if(this.homeCrossSectionScale < 0.001) {
          this.homeCrossSectionScale = 0.001;
        }
        scaleControl.restoreMin(this.homeCrossSectionScale * this.minScaleFactor);
        if (!this.isCompleted() && !this.navigatorInitialized) {//没有初始化过缩略图导航工具时，需要先设置缩放比例
          scale.value = this.homeCrossSectionScale;
          // 针对demo示例需设置为设置的缩放比例
          if (_param.flag === 'demo') {
            setTimeout(() => {
              this.crossSectionScale.value = this.initCrossSectionScaleForDemoData
            }, 300);
          }
        }
        SliceViewPanel.scaleMax = 1 / (this.homeCrossSectionScale * this.minScaleFactor);
      }
    };

    let needCreateOverlay = true;
    this.dataContext.chunkQueueManager.visibleChunksChanged.add(()=>{
      if (needCreateOverlay) {
        needCreateOverlay = false;
        navigatorWidget.createOverlay();
      }
    });

    const checkAllLayersBeenDrawn = this.registerCancellable(debounce(()=>{
      if (!this.navigatorInitialized && this.isCompleted()) {
        setScaleRangeFn();
        this.navigatorInitialized = true;
        this.navigationState.position.reset();
        scale.value = this.homeCrossSectionScale;
        // 针对demo示例需设置为设置的缩放比例
        if (_param.flag === 'demo') {
          setTimeout(() => {
            this.crossSectionScale.value = this.initCrossSectionScaleForDemoData
          }, 300);
        }
        this.dataContext.chunkQueueManager.visibleChunksChanged.remove(checkAllLayersBeenDrawn);
        //记录撤销操作所需的历史记录
        this.resetJson = JSON.parse(JSON.stringify( viewer.state.toJSON() ));
        navigatorWidget.initThumbnail();
      }
    },500));
    this.dataContext.chunkQueueManager.visibleChunksChanged.add(checkAllLayersBeenDrawn);

    // 当页面切换到后台时，如果chunk已经全部触发完成，切缩略图还没有绘制，此时前台激活的时候需要重新绘制一下缩略图
    document.addEventListener('visibilitychange', () => {
      if(document.visibilityState === 'visible'){
        checkAllLayersBeenDrawn()
      }
    })
    this.dataContext.chunkQueueManager.visibleChunksChanged.add(this.registerCancellable(throttle(() => {
      // const chunkStatistics = {
      //   totalPresent: 0,
      //   totalNotPresent: 0,
      //   totalChunk: 0
      // }
      // const {totalPresent, totalChunk} = this.chunkStatistics
      // totalPresent.value = 0
      // totalChunk.value = 0
      // for (const layer of this.layerManager.managedLayers) {
      //   if (layer.visible && layer?.chunkStatistics) {
      //     const {chunkStatistics:layerChunkStatistics} = layer
      //     totalPresent.value += layerChunkStatistics.totalPresent || 0
      //     // chunkStatistics.totalNotPresent += layerChunkStatistics.totalNotPresent || 0
      //     totalChunk.value += layerChunkStatistics.totalChunk || 0
      //   }
      // }
      // console.log('viewer--', this.chunkStatistics)
      // console.log(`totalChunk:${totalChunk.value},totalPresent:${totalPresent.value}`)
      new LoadingProgress(this)
    },200)));

    const setScaleRange = this.registerCancellable(debounce(setScaleRangeFn, 100));
    this.registerDisposer(this.display.resize.add(setScaleRange));
    const leftButons = document.createElement('div');
    leftButons.classList.add('neuroglancer-mulBtns', 'column');
    leftButons.style.display = userPemission.statusbar ? 'block' : 'none';
    centerLeft.appendChild(leftButons)
    // 保存
    if (!local_href_param.isReadOnly) {
      {
        const button = makeIcon({
          svg: svg_save,
          title: i18n.save,
          classList: ['status-save'],
          // disabled: true
          throttleClick: () => {
            console.log('保存')
            new StateEdit(this, 'save');
          }
        });
        this.pemission.register(button, 'saveState');
        // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
        //     this.uiControlVisibility.showEditStateButton, button));
        leftButons.appendChild(button);
      }
    }
    // 读取
    const loadContatindiv = makeElement('div',['neuroglancer-icon-status_lasso-div']);
    const loadContatin = document.createElement('ul');
    loadContatindiv.appendChild(loadContatin);
    loadContatin.classList.add('neuroglancer-icon-status_lasso')
    const statu_div = makeElement('li',['statu-load-div'],{style: 'margin: 0.5rem'});
    const lasso_div = makeElement('li',[],{style: 'margin: 0.5rem'});
    if(userPemission.lasso)loadContatin.appendChild(lasso_div);
    if(userPemission.saveState)loadContatin.appendChild(statu_div);
    {
      loadContatin.classList.add('load-status-lasso');
      const statu_icon = makeIcon({
        svg: svg_status_load,
        // title: i18n.load,
        classList: ['status-load'],
        throttleClick: () => {
          console.log('读取')
          new StateEdit(viewer, 'status_record');
        }
      });
      const statu_span = makeElement('span',[],{},`${i18n.statu_span}`);
      const lasso_icon = makeIcon({
        svg: svg_lasso_load,
        // title: i18n.lassoload,
        classList: ['lasso-load'],
        throttleClick: () => {
          console.log('读取')
          new StateEdit(viewer, 'lasso_record');
        }
      });
      const lasso_span = makeElement('span',[],{},`${i18n.lasso_span}`);
      statu_div.appendChild(statu_icon);
      statu_div.appendChild(statu_span);
      lasso_div.appendChild(lasso_icon);
      lasso_div.appendChild(lasso_span);
      // if(!userPemission.lasso)lasso_div.style.display = 'none';
      lasso_div.addEventListener('click',async ()=>{
        if(!(await check_balance()))return
        new StateEdit(viewer, 'lasso_record');
      });
      statu_div.addEventListener('click',()=>{
        new StateEdit(viewer, 'status_record');
      });
      const {layersVisibleChanged} = this.layerManager;
      layersVisibleChanged.add(() => {
        const hasCluster = this.layerManager.getLayerVisible(['cluster']);
        const hasHeatmap = this.layerManager.getLayerVisible(['heatmap']);
        lasso_div.style.display = ( (hasCluster && viewer.binSize.value === 'cellbin') || hasHeatmap)? 'flex' : 'none';
        if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
          loadContatin.style.display = 'none';
        } else{
          loadContatin.style.display = '';
        }

      })
      this.binSize.changed.add(() => {
        const hasCluster = this.layerManager.getLayerVisible(['cluster']);
        const hasHeatmap = this.layerManager.getLayerVisible(['heatmap']);
        lasso_div.style.display = ( (hasCluster && viewer.binSize.value === 'cellbin') || hasHeatmap)? 'flex' : 'none';
        if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
          loadContatin.style.display = 'none';
        } else{
          loadContatin.style.display = '';
        }
      });
      loadContatin.addEventListener('mouseenter',()=>{
        document.getElementsByClassName('all-load')[0]?.classList.add('all-load-status-lasso')
      })
      loadContatin.addEventListener('mouseleave',()=>{
        document.getElementsByClassName('all-load')[0]?.classList.remove('all-load-status-lasso')
      })
    }
    // 读取
    if (!local_href_param.isReadOnly) {
      {
        const button = makeIcon({
          svg: svg_load,
          // title: i18n.load,
          classList: ['all-load'],
          checked: 'false',
          throttleClick: () => {},
          onClick(){}
        });
        button.id = 'all-load'
        // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
          //     this.uiControlVisibility.showEditStateButton, button));
        leftButons.appendChild(button);
        leftButons.appendChild(loadContatindiv);
        if(local_href_param?.visible)button.style.display = 'none'
      }
    }
    // {
    //   const button = makeIcon({
    //     svg: svg_load,
    //     title: i18n.load,
    //     classList: ['status-load'],
    //     // disabled: true
    //     throttleClick: () => {
    //       console.log('读取')
    //       new StateEdit(this, 'load');
    //       disableStatus(false);
    //     }
    //   });
    //   this.pemission.register(button, 'saveState');
    //   // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
    //   //     this.uiControlVisibility.showEditStateButton, button));
    //   leftButons.appendChild(button);
    // }
    // 导出图片
    if(userPemission.statusbar){
      {
        const button = makeIcon({
          svg: svg_export,
          title: i18n.download,
          classList: ['status-export'],
          // disabled: true
          throttleClick: () => {
            console.log('导出图片')
            // if(this.isCompleted()) {
              this.exportImageState();
            // } else {
            //   new message('The current data has not been loaded, please wait until the data is loaded before using the HD image export function!', 'warning')
            // }
          }
        });
        this.pemission.register(button, 'exportImage')
        // this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
        //     this.uiControlVisibility.showEditStateButton, button));
        centerLeft.appendChild(button);
      }
    }

    // this.layout = this.registerDisposer(new RootLayoutContainer(this, 'xy'));

    // this.sidePanelManager = this.registerDisposer(
    //   new SidePanelManager(this.display, this.layout.element, this.visibility));
    // layerlist
    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.layerListPanelState.location,
      makePanel: () =>
        new LayerListPanel(this.sidePanelManager, this.layerSpecification, this.layerListPanelState),
    }));


    this.registerDisposer(
      new LayerSidePanelManager(this.sidePanelManager, this.selectedLayer.addRef(), this.configStorage));
    // detail
    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.selectionDetailsState.location,
      makePanel: () => new SelectionDetailsPanel(
        this.sidePanelManager, this.selectionDetailsState, this.layerSpecification,
        this.selectedLayer),
    }));
    gridContainer.appendChild(this.sidePanelManager.element);

    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.statisticsDisplayState.location,
      makePanel: () => new StatisticsPanel(
        this.sidePanelManager, this.chunkQueueManager, this.statisticsDisplayState),
    }));
    // help
    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.helpPanelState.location,
      makePanel: () => {
        const { inputEventBindings } = this;
        return new InputEventBindingHelpDialog(
          this.sidePanelManager,
          this.helpPanelState,
          [
            ['Global', inputEventBindings.global],
            ['Cross section view', inputEventBindings.sliceView],
            ['3-D projection view', inputEventBindings.perspectiveView]
          ],
          this.layerManager,
          this.toolBinder,
        );
      },
    }));
    // setting
    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.settingsPanelState.location,
      makePanel: () =>
        new ViewerSettingsPanel(this.sidePanelManager, this.settingsPanelState, this),
    }));
    // // genetable
    // this.registerDisposer(this.sidePanelManager.registerPanel({
    //   location: this.genePanelState.location,
    //   makePanel: () =>
    //     new GeneTabView(this.sidePanelManager, this.genePanelState, this.layerSpecification),
    // }));
    // protein
    this.registerDisposer(this.sidePanelManager.registerPanel({
      location: this.proteinPanelState.location,
      makePanel: () =>
        new proteinTabView(this.sidePanelManager, this.proteinPanelState, this.layerSpecification,
          this.selectedLayer),
    }));

    const updateVisibility = () => {
      const shouldBeVisible = this.visibility.visible;
      if (shouldBeVisible !== this.visible) {
        gridContainer.style.visibility = shouldBeVisible ? 'inherit' : 'hidden';
        this.visible = shouldBeVisible;
      }
    };
    updateVisibility();
    this.registerDisposer(this.visibility.changed.add(updateVisibility));
  }
  initClusterSelectView(){
    if (this.cluterView) {
      const {managedLayers} = this.layerManager
      const clusterlayer = managedLayers.filter((layer) => {
        return hasCluster(layer.name) && layer.visible
      })
      if (clusterlayer.length > 0 && (clusterlayer[0].layer instanceof SegmentationUserLayer || clusterlayer[0].layer instanceof AnnotationUserLayer)) {
        this.curSegmentDisplayTab = new SegmentDisplayTab(clusterlayer[0].layer)
        this.cluterView.element?.appendChild(this.curSegmentDisplayTab.element)
      }
    }
  }
  resetoreJson(obj: any){
    // 当撤回被禁用时，存储上一步的操作不给起作用
    if(!document.getElementsByClassName('tools-undo')[0].classList.contains('disabled')){
      if(this.jsonCloneObj.length > 10) this.jsonCloneObj.splice(0, 1);
      obj = JSON.parse(JSON.stringify(obj));
      obj.proteinMergeColor = viewer.proteinMergeColor.value;
      if(!obj.hasOwnProperty('crossSectionOrientation')){
        obj['crossSectionOrientation'] = [0,0,-2.523557895983686e-7,1]
      }
      this.jsonCloneObj.push(obj);
      console.log(this.jsonCloneObj)
    }
  }

  /**
   * Called once by the constructor to set up event handlers.
   */
  private registerEventActionBindings() {
    const { element } = this;
    this.registerDisposer(new KeyboardEventBinder(element, this.inputEventMap));
    this.registerDisposer(new AutomaticallyFocusedElement(element));
  }

  bindAction<Data>(action: string, handler: (event: ActionEvent<Data>) => void) {
    this.registerDisposer(registerActionListener(this.element, action, handler));
  }

  /**
   * Called once by the constructor to register the action listeners.
   */
  private registerActionListeners() {
    for (const action of ['recolor', 'clear-segments']) {
      this.bindAction(action, () => {
        this.layerManager.invokeAction(action);
      });
    }

    for (const action of ['select']) {
      this.bindAction(action, () => {
        this.mouseState.updateUnconditionally();
        this.layerManager.invokeAction(action);
      });
    }

    this.bindAction('help', () => this.toggleHelpPanel());

    for (let i = 1; i <= 9; ++i) {
      this.bindAction(`toggle-layer-${i}`, () => {
        const layer = this.layerManager.getLayerByNonArchivedIndex(i - 1);
        if (layer !== undefined) {
          layer.setVisible(!layer.visible);
        }
      });
      this.bindAction(`toggle-pick-layer-${i}`, () => {
        const layer = this.layerManager.getLayerByNonArchivedIndex(i - 1);
        if (layer !== undefined) {
          layer.pickEnabled = !layer.pickEnabled;
        }
      });
      this.bindAction(`select-layer-${i}`, () => {
        const layer = this.layerManager.getLayerByNonArchivedIndex(i - 1);
        if (layer !== undefined) {
          this.selectedLayer.layer = layer;
          this.selectedLayer.visible = true;
        }
      });
    }

    for (let i = 0; i < 26; ++i) {
      const uppercase = String.fromCharCode(65 + i);
      this.bindAction(`tool-${uppercase}`, () => {
        this.activateTool(uppercase);
      });
    }

    this.bindAction('annotate', () => {
      const selectedLayer = this.selectedLayer.layer;
      if (selectedLayer === undefined) {
        StatusMessage.showTemporaryMessage('The annotate command requires a layer to be selected.');
        return;
      }
      const userLayer = selectedLayer.layer;
      if (userLayer === null || userLayer.tool.value === undefined) {
        StatusMessage.showTemporaryMessage(`The selected layer (${JSON.stringify(selectedLayer.name)}) does not have an active annotation tool.`);
        return;
      }
      userLayer.tool.value.trigger(this.mouseState);
    });

    this.bindAction('toggle-axis-lines', () => this.showAxisLines.toggle());
    this.bindAction('toggle-scale-bar', () => this.showScaleBar.toggle());
    this.bindAction('toggle-default-annotations', () => this.showDefaultAnnotations.toggle());
    this.bindAction('toggle-show-slices', () => this.showPerspectiveSliceViews.toggle());
    this.bindAction('toggle-show-statistics', () => this.showStatistics());
    // ctrl+alt+equal图层放大
    this.bindAction('layer-zoom-in', () => this.layerZoom());
    this.bindAction('layer-zoom-out', () => this.layerZoom());
    // ctrl+alt+arrowright图层顺时针旋转
    this.bindAction('layer-clockwise-rotation', () => this.rotateRelativeAboutZ(-5));
    // ctrl+alt+arrowLeft图层逆时针旋转
    this.bindAction('layer-anticlockwise-rotation', () => this.rotateRelativeAboutZ(5));
    // ctrl+alt+鼠标左键拖曳图层
    this.bindAction('move-layer-via-mouse-drag', (e: ActionEvent<MouseEvent>) => {
      const selectedLayer = this.selectedLayer.layer;
      if (selectedLayer === undefined) {
        StatusMessage.showTemporaryMessage('The move layer via mouse drag command requires a layer to be selected.');
        return;
      }
      if (selectedLayer.name.slice(-9) === 'transform') {
        StatusMessage.showTemporaryMessage('Please use the right panel to move layer or turn off manual registration.');
        return;
      }
      const {
        zoomFactor: { value: zoom },
      } = viewer.navigationState;
      const extTransform = (selectedLayer.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.transform;
      if (extTransform.length === 16) {
        const newTransform = mat4.fromValues(extTransform[0], extTransform[1], extTransform[2], extTransform[3],
          extTransform[4], extTransform[5], extTransform[6], extTransform[7],
          extTransform[8], extTransform[9], extTransform[10], extTransform[11],
          extTransform[12], extTransform[13], extTransform[14], extTransform[15])
        let translateMat
        startRelativeMouseDrag(e.detail, (_event, deltaX, deltaY) => {
          translateMat = mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, round(deltaX * zoom), round(deltaY * zoom), 0, 1)
          mat4.multiply(newTransform, translateMat, newTransform);
          (selectedLayer?.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(newTransform);
          this.saveRegistParamXY(deltaX, deltaY)
        });
      } else {
        const newTransform = mat3.fromValues(extTransform[0], extTransform[1], extTransform[2], extTransform[3],
          extTransform[4], extTransform[5], extTransform[6], extTransform[7],
          extTransform[8])
        let translateMat
        startRelativeMouseDrag(e.detail, (_event, deltaX, deltaY) => {
          translateMat = mat3.fromValues(1, 0, 0, 0, 1, 0, round(deltaX * zoom), round(deltaY * zoom), 1)
          mat3.multiply(newTransform, translateMat, newTransform);
          (selectedLayer?.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(newTransform);
          this.saveRegistParamXY(deltaX, deltaY)
        });
      }
    })
  }
  // 初始化配置
  initConfigState () {
    const {configStorage} = this

    // 清空配置
    configStorage.clear()
    configStorage.setActivedTool('')

    // 重置时初始化聚类
    // const {element} = this.cluterView
    // if (element && element.children.length > 1) {
    //   element.removeChild(element.children[1])
    //   this.cluterView.setState(false)
    // }

  }
  layerZoom(scalex: number = 0.01, scaley: number = 0.01) {
    const selectedLayer = this.selectedLayer.layer;
    if (selectedLayer === undefined) {
      StatusMessage.showTemporaryMessage('The layer zoom in or out command requires a layer to be selected.');
      return;
    }
    if (selectedLayer.type === 'segmentation') {
      StatusMessage.showTemporaryMessage('The cellbin layer can\'t be zoomed.');
      return
    }
    let extTransform = (selectedLayer.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.transform;
    // if (layerOriginTransforams.get(selectedLayer!.layer!.managedLayer.name)) {
    //   extTransform = (layerOriginTransforams.get(selectedLayer!.layer!.managedLayer.name) as Float64Array)
    // } else {
    //   layerOriginTransforams.set(selectedLayer!.layer!.managedLayer.name, extTransform)
    // }
    // 默认缩放倍数为0.5和2
    // const zoomSize = type === 'in' ? 2 : 0.5;
    let zoomX = scalex;
    let zoomY = scaley;
    // if(type) zoomX = zoomY = type === 'in' ? 2 : 0.5;
    if ((extTransform.length === 16)) {
      const zoomMat = mat4.fromValues(zoomX, 0, 0, 0, 0, zoomY, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)
      const newTransform = mat4.fromValues(extTransform[0], extTransform[1], extTransform[2], extTransform[3],
        extTransform[4], extTransform[5], extTransform[6], extTransform[7],
        extTransform[8], extTransform[9], extTransform[10], extTransform[11],
        extTransform[12], extTransform[13], extTransform[14], extTransform[15])
      let _bounds: any = [];
      for (const a of (selectedLayer?.layer_ as ImageUserLayer)?.channelCoordinateSpaceCombiner.bindings) {
        _bounds = a.space.value.bounds;
      }
      //缩放
      mat4.multiply(newTransform, zoomMat, newTransform);
      // 缩放中心位移量
      const translate_x = (1 - zoomX) * 0.5 * (_bounds.upperBounds[0] + _bounds.lowerBounds[0])
      const translate_y = (1 - zoomY) * 0.5 * (_bounds.upperBounds[1] + _bounds.lowerBounds[1])
      // 平移
      const translateMat = mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, translate_x, translate_y, 0, 1)
      mat4.multiply(newTransform, translateMat, newTransform);
      (selectedLayer.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(newTransform);
    } else {
      const zoomMat = mat3.fromValues(zoomX, 0, 0, 0, zoomY, 0, 0, 0, 1)
      const newTransform = mat3.fromValues(extTransform[0], extTransform[1], extTransform[2], extTransform[3],
        extTransform[4], extTransform[5], extTransform[6], extTransform[7],
        extTransform[8])
      let _bounds: any = [];
      for (const a of (selectedLayer?.layer_ as UserLayer)?.localCoordinateSpaceCombiner.bindings) {
        _bounds = a.space.value.bounds;
      }
      //缩放
      mat3.multiply(newTransform, zoomMat, newTransform);
      // 缩放中心位移量
      const translate_x = (1 - zoomX) * 0.5 * (_bounds.upperBounds[0] + _bounds.lowerBounds[0])
      const translate_y = (1 - zoomY) * 0.5 * (_bounds.upperBounds[1] + _bounds.lowerBounds[1])
      // 平移
      const translateMat = mat3.fromValues(1, 0, 0, 0, 1, 0, translate_x, translate_y, 1)
      mat3.multiply(newTransform, translateMat, newTransform);
      (selectedLayer.layer_?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(newTransform);
    }
    // this.saveZoomSize(zoomSize)
  }
  //改变配准图像的旋转中心，入参向量为旋转中心处于原始图像上的坐标
  changeImageAdjustOrigin(originPointPosOnImage: vec3) {
    this.transform_sign_point = originPointPosOnImage;
    let projectPosV3 = vec3.clone(originPointPosOnImage);
    vec3.transformMat4(projectPosV3, projectPosV3, this.imageSourceModelMat4);
    let originPointAfterTransformV3 = vec3.clone(this.transform_sign_point);
    this.newLayerRotate();

    vec3.transformMat4(originPointAfterTransformV3, originPointAfterTransformV3, this.imageSourceModelMat4);
    let offsetV3 = vec3.create();
    vec3.sub(offsetV3, projectPosV3, originPointAfterTransformV3);
    registParam.OffsetX = registParam.OffsetX + offsetV3[0];
    registParam.OffsetY = registParam.OffsetY + offsetV3[1];
    this.newLayerRotate();
  }
  setOriginToImageCenter() {
    const originX = 0.5 * ( (registParam.bound as any).upperBounds[0] + ( registParam.bound as any).lowerBounds[0]);
    const originY = 0.5 * ( (registParam.bound as any).upperBounds[1] + ( registParam.bound as any).lowerBounds[1]);
    this.changeImageAdjustOrigin(vec3.fromValues(originX, originY, 0))
  }
  unprojectMousePosToImage(mousePos:vec3) {
    let subSpaceV3 = vec3.clone(mousePos);
    vec3.transformMat4(subSpaceV3, subSpaceV3, viewer.imageSourceModelMat4Inverse);
    return subSpaceV3;
  }
  newLayerRotate(){
    let new_imageParam = {
      deg: registParam?.deg ?? 0,
      scalex: registParam?.scalex ?? 1,
      scaley: registParam?.scaley ?? 1,
      bound: registParam.bound,
      filp: registParam?.filp ?? 0,
      OffsetX: registParam?.OffsetX ?? 0,
      OffsetY: registParam?.OffsetY ?? 0
    }
    const selectedLayer = this.selectedLayer.layer;
    const handleRotate  = (extTransform: Float64Array) => {
      // 特征点坐标，特征点是旋转中心，也是翻转轴穿过的点，翻转轴垂直于x轴
      const transform_o_x = viewer.transform_sign_point[0] ? Math.abs( viewer.transform_sign_point[0]) : 0;
      const transform_o_y = viewer.transform_sign_point[1] ? Math.abs( viewer.transform_sign_point[1]) : 0;

      if ((extTransform?.length === 16 || extTransform?.length === 25)) {
        let rotateMatrix = mat4.create();
        mat4.rotateZ(rotateMatrix, rotateMatrix, -new_imageParam.deg*Math.PI/180);
        let scaleMatrix = mat4.fromScaling(mat4.create(),vec3.fromValues(new_imageParam.scalex, new_imageParam.scaley, 1));

        const flipMartix = mat4.fromScaling(mat4.create(), vec3.fromValues(new_imageParam.filp ? -1 : 1, 1, 1));

        //反向平移矩阵
        let negTransMatrix = mat4.create();
        mat4.fromTranslation(negTransMatrix, vec3.fromValues(-transform_o_x, -transform_o_y, 0));

        //正向平移矩阵
        let transMatrix = mat4.create();
        mat4.fromTranslation(transMatrix, vec3.fromValues(transform_o_x, transform_o_y, 0));
        // 位移
        let weiyiMartix = mat4.create();
        mat4.fromTranslation(weiyiMartix, vec3.fromValues(new_imageParam.OffsetX, new_imageParam.OffsetY, 0))

        let resultMatrix = mat4.create();
        mat4.mul(resultMatrix, scaleMatrix, negTransMatrix);
        mat4.mul(resultMatrix, flipMartix, resultMatrix);
        mat4.mul(resultMatrix, rotateMatrix, resultMatrix);
        mat4.mul(resultMatrix, transMatrix, resultMatrix);
        mat4.mul(resultMatrix, weiyiMartix, resultMatrix);

        this.imageSourceModelMat4 = mat4.clone(resultMatrix);
        mat4.invert(this.imageSourceModelMat4Inverse, resultMatrix);

        if(extTransform?.length === 16){
          return new Float64Array(resultMatrix)
        }else{
          let resultMatrix_4_jie: Float64Array = new Float64Array(resultMatrix);
          const resultMatrix_5_jie: Float64Array = new Float64Array([
            resultMatrix_4_jie[0], resultMatrix_4_jie[1], resultMatrix_4_jie[2], resultMatrix_4_jie[3], resultMatrix_4_jie[12],
            resultMatrix_4_jie[4], resultMatrix_4_jie[5], resultMatrix_4_jie[6], resultMatrix_4_jie[7], resultMatrix_4_jie[13],
            resultMatrix_4_jie[8], resultMatrix_4_jie[9], resultMatrix_4_jie[10], resultMatrix_4_jie[11], resultMatrix_4_jie[14],
            0, 0, 0, 1, 0,
            resultMatrix_4_jie[12],resultMatrix_4_jie[13],resultMatrix_4_jie[14],0,1
          ]);
          return new Float64Array(resultMatrix_5_jie);
        }
      } else {
        let rotateMatrix = mat3.create();
        mat3.rotate(rotateMatrix, rotateMatrix, -new_imageParam.deg*Math.PI/180);
        let scaleMatrix = mat3.fromScaling(mat3.create(),[new_imageParam.scalex, new_imageParam.scaley]);

        const flipMartix = mat3.fromScaling(mat3.create(), [new_imageParam.filp ? -1 : 1, 1]);

        //反向平移矩阵
        let negTransMatrix = mat3.create();
        mat3.fromTranslation(negTransMatrix, [-transform_o_x, -transform_o_y]);
        //正向平移矩阵
        let transMatrix = mat3.create();
        mat3.fromTranslation(transMatrix, [transform_o_x, transform_o_y]);
        let resultMatrix = mat3.create();
        mat3.mul(resultMatrix, scaleMatrix, negTransMatrix);
        mat3.mul(resultMatrix, rotateMatrix, resultMatrix);
        mat3.mul(resultMatrix, flipMartix, resultMatrix);
        mat3.mul(resultMatrix, transMatrix, resultMatrix);

        return new Float64Array(resultMatrix)
      }
    }
    let extTransform = (selectedLayer?.layer?.dataSources[0].loadState_ as LoadedLayerDataSource)?.transform.value.transform;
    if(extTransform)(selectedLayer!.layer?.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(handleRotate(extTransform));
  }
  //将sliceView相机z轴旋转到某一个角度，
  getCurrentOrientationDeg(){
    //等同于viewer.crossSectionOrientation
    let orientation = this.navigationState.pose.orientation.orientation;
    let xDir = vec3.fromValues(0,0,1);
    let curAngle = quat.getAxisAngle(xDir,orientation);
    if (curAngle) {
      if(xDir[2] < 0){
        curAngle = -curAngle *  180 /Math.PI;
      } else {
        curAngle = curAngle * 180 / Math.PI;
      }
    } else {
      curAngle = 0;
    }
    return curAngle;
  }
  //将sliceView相机z轴旋转到某一个角度，
  rotateZ_axisTo(customDeg: number = 0) {
    let curAngle = this.getCurrentOrientationDeg();
    let deltaAngleDeg = customDeg - curAngle;
    if (Math.abs(deltaAngleDeg) > 0.01) {
      //ng定义逆时针为正
      this.navigationState.pose.rotateRelative(vec3.fromValues(0,0,1), deltaAngleDeg * Math.PI/180 )
    }
    // if (customDeg === 0) {
    //   viewer.globalTransform = undefined
    // }
  }
  //sliceView相机绕z轴旋转
  rotateRelativeAboutZ(customDeg: number = 5) {
    this.navigationState.pose.rotateRelative(vec3.fromValues(0,0,1), customDeg * Math.PI/180 )
    }
  //保存位移量
  saveRegistParamXY(deltaX: number, deltaY: number) {
    const selectedLayer = this.selectedLayer.layer;
    if (selectedLayer) {
      const {
        zoomFactor: { value: zoom },
      } = viewer.navigationState;
      if (viewer.registParam[selectedLayer.name]) {
        viewer.registParam[selectedLayer.name].OffsetX = viewer.registParam[selectedLayer.name].OffsetX + round(deltaX * zoom)
        viewer.registParam[selectedLayer.name].OffsetY = viewer.registParam[selectedLayer.name].OffsetY + round(deltaY * zoom)
      } else {
        viewer.registParam[selectedLayer.name] = { OffsetX: round(deltaX * zoom), OffsetY: round(deltaY * zoom) };
      }
    }
  }

  //保存当前旋转角度
  saveRotateDeg(deg: number) {
    const selectedLayer = this.selectedLayer.layer;
    if (selectedLayer) {
      if (viewer.registParam[selectedLayer.name]) {
        const rotateDeg = viewer.registParam[selectedLayer.name].rotateDeg
        if (rotateDeg) {
          viewer.registParam[selectedLayer.name].rotateDeg = (rotateDeg + deg) % 360
        } else {
          viewer.registParam[selectedLayer.name].rotateDeg = deg
        }
      } else {
        viewer.registParam[selectedLayer.name] = { OffsetX: 0, OffsetY: 0, rotateDeg: deg };
      }
    }
  }

  //保存当前缩放倍数
  saveZoomSize(size: number) {
    const selectedLayer = this.selectedLayer.layer;
    if (selectedLayer) {
      if (viewer.registParam[selectedLayer.name]) {
        const zoomSize = viewer.registParam[selectedLayer.name].zoomSize
        if (zoomSize) {
          viewer.registParam[selectedLayer.name].zoomSize = zoomSize * size
        } else {
          viewer.registParam[selectedLayer.name].zoomSize = size
        }
      } else {
        viewer.registParam[selectedLayer.name] = { OffsetX: 0, OffsetY: 0, zoomSize: size };
      }
    }
  }



  toggleHelpPanel() {
    this.helpPanelState.location.visible = !this.helpPanelState.location.visible;
  }

  private toolInputEventMapBinder = (inputEventMap: EventActionMap, context: RefCounted) => {
    context.registerDisposer(
      this.inputEventBindings.sliceView.addParent(inputEventMap, Number.POSITIVE_INFINITY));
    context.registerDisposer(this.inputEventBindings.perspectiveView.addParent(
      inputEventMap, Number.POSITIVE_INFINITY));
  };

  private toolBinder = this.registerDisposer(new ToolBinder(this.toolInputEventMapBinder));

  activateTool(uppercase: string) {
    this.toolBinder.activate(uppercase);
  }

  editJsonState() {
    new StateEditorDialog(this);
  }

  exportImageState() {
    new ExportImageDialog(this);
  }

  showStatistics(value: boolean | undefined = undefined) {
    if (value === undefined) {
      value = !this.statisticsDisplayState.location.visible;
    }
    this.statisticsDisplayState.location.visible = value;
  }

  get gl() {
    return this.display.gl;
  }

  onUpdateDisplay() {
    if (this.visible) {
      this.dataContext.chunkQueueManager.chunkUpdateDeadline = null;
    }
  }

  private handleNavigationStateChanged() {
    if (this.visible) {
      let { chunkQueueManager } = this.dataContext;
      if (chunkQueueManager.chunkUpdateDeadline === null) {
        chunkQueueManager.chunkUpdateDeadline = Date.now() + 10;
      }
    }
  }

  isCompleted() {
    for (const layer of this.layerManager.managedLayers) {
      if (layer.visible && !layer.isCompleted) {
        return false;
      }
    }
    return true;
  }
}

function IFSubmitBox(){
  let IFlayerSize:any = [];
  let IFstaticAnnotations: AnnotationSource | undefined = undefined;
  let IFMarkArr:any = [];
  let ssDNASize:any = [];
  let ssDNAAnnotationSource: AnnotationSource | undefined = undefined;
  let ssDNAArr:any = [];
  for(let i = 0; i < viewer.layerManager.managedLayers.length; i++){
    if(viewer.layerManager.managedLayers[i].name.toLocaleLowerCase().includes('ssdna')){
      ssDNASize = ( (viewer.layerManager.managedLayers[i].layer as UserLayer).dataSources[0]?.loadState_ as LoadedLayerDataSource)?.dataSource?.modelTransform?.inputSpace?.bounds?.upperBounds ?? 0;
      if(ssDNASize.length > 0){
        ssDNAAnnotationSource = ( (viewer.layerManager.managedLayers[i].layer as UserLayer)?.dataSources[0]?.loadState_ as LoadedLayerDataSource)?.subsources[2].subsourceEntry.subsource.staticAnnotations as AnnotationSource;
        if(ssDNAAnnotationSource){
          for(let value of ssDNAAnnotationSource.annotationMap){
            if(value[1].type === 0){
              ssDNAArr.push([value[1].point[0], value[1].point[1]])
            }
          }
        }
      }
    }else if(viewer.layerManager.managedLayers[i].name.toLocaleLowerCase().includes('_if')){
      IFlayerSize = ( (viewer.layerManager.managedLayers[i].layer as UserLayer).dataSources[0]?.loadState_ as LoadedLayerDataSource)?.dataSource?.modelTransform?.inputSpace?.bounds?.upperBounds ?? 0;
      if(IFlayerSize.length > 0){
        IFstaticAnnotations = ( (viewer.layerManager.managedLayers[i].layer as UserLayer)?.dataSources[0]?.loadState_ as LoadedLayerDataSource)?.subsources[2].subsourceEntry.subsource.staticAnnotations as AnnotationSource;
        if(IFstaticAnnotations){
          for(let valueIF of IFstaticAnnotations.annotationMap){
            if(valueIF[1].type === 0){
              IFMarkArr.push([valueIF[1].point[0], valueIF[1].point[1]])
            }
          }
        }
      }
    }
  }
  return {ssDNASize, IFlayerSize, ssDNAArr, IFMarkArr, ssDNAAnnotationSource, IFstaticAnnotations};
}

function beforeActiveTool() {
  const {managedLayers}  = viewer.layerManager
  const umapLayer = managedLayers.find((layer:any) => layer.name.includes('UMAP'))
  umapLayer?.setVisible(false)
}
// 所有工具按钮统一操作
export function isToolControl(flag: string, str?: Array<any>){    //  flag: add or remove,  str: 不需要处理的class名称
  const class_list: string[] = ['measure_btn', 'lassoBtn', 'neuroglancer-icon-transform', 'tools-undo', 'neuroglancer-icon-standardAnalysis'];           //  存储所有需要控制的class的名称
  // 1. 将所有的工具按钮都进行flag处理，单独对str做false处理
  for(let i = 0; i < class_list.length; i++){
    if(document.getElementsByClassName(class_list[i]) && document.getElementsByClassName(class_list[i])[0]){
      if(viewer.proteinMergeColor.value && (class_list[i] === 'neuroglancer-icon-transform' || class_list[i] === 'lassoBtn'))continue;
      if(flag === 'remove'){
        if(class_list[i] === 'neuroglancer-icon-transform' && ( !allToolsButton.isTransform || viewer.binSize.value === 'cellbin'))continue;
        if(class_list[i] === 'neuroglancer-icon-standardAnalysis' && (!allToolsButton.isStandardAnalysis || viewer.binSize.value === 'cellbin'))continue;
        if(class_list[i] === 'lassoBtn' && (viewer.geoRegistrationTool))continue;
      }
      document.getElementsByClassName(class_list[i])[0].classList[flag]('disabled')
    }
  }
  if(str){
    for(let i = 0; i < str.length; i++){
      if(document.getElementsByClassName(str[i]) && document.getElementsByClassName(str[i])[0]){
        if(str[i] === 'neuroglancer-icon-transform' && ( !allToolsButton.isTransform || viewer.binSize.value === 'cellbin')) continue;
        if(str[i] === 'neuroglancer-icon-standardAnalysis' && (!allToolsButton.isStandardAnalysis || viewer.binSize.value === 'cellbin'))continue;
        document.getElementsByClassName(str[i])[0].classList.remove('disabled')
      }
    }
  }
  if (document.getElementsByClassName('neuroglancer-icon-transform') && document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true') {
    (document.getElementsByClassName('measure_btn')[0] as HTMLElement).classList['add']("disabled");
  }
  if (document.getElementsByClassName('measure_btn') && document.getElementsByClassName('measure_btn')[0] && document.getElementsByClassName('measure_btn')[0].getAttribute('data-checked') === 'true') {
    if(document.getElementsByClassName('neuroglancer-icon-transform') && document.getElementsByClassName('neuroglancer-icon-transform')[0]){
      (document.getElementsByClassName('neuroglancer-icon-transform')[0] as HTMLElement).classList['add']("disabled");
      (document.getElementsByClassName('neuroglancer-icon-transform')[0] as HTMLElement).setAttribute('data-checked', 'false')
    }
  }
}

export function isSelectView(flag: boolean, str?:string | string[]) {
  // 除了字符串中的需要做flag处理，其余的都需要做flag相反的处理
  let doms1 = document.querySelectorAll('.neuroglancer-viewer-center-top-row .neuroglancer-selecteView');
  for(let i = 0; i < doms1.length; i++){
    if((doms1[i] as HTMLElement).getAttribute('data-checked')=='true') {
      (doms1[i] as HTMLElement).click();
    }
    if(str && typeof str === 'string'){
      const newflag = doms1[i].classList.contains(str) ? true : flag;
      (doms1[i] as HTMLElement).classList[newflag ? 'remove': 'add'](doms1[i].classList.contains('tools-setting') ? "disabled" : "lasso_disabled");
    }else if(str && typeof str === 'object'){
      for(let j = 0; j < str.length ; j++){
        if(doms1[i].classList.contains(str[j])){
          const newflag = doms1[i].classList.contains(str[j]) ? true : flag;
          // console.log(doms1[i].classList.contains(str[j]), str[j], doms1[i], newflag);
          (doms1[i] as HTMLElement).classList[newflag ? 'remove': 'add']("lasso_disabled");     }else{
          (doms1[i] as HTMLElement).classList[flag ? 'remove': 'add']("lasso_disabled");
        }
      }
    }else{
      (doms1[i] as HTMLElement).classList[flag ? 'remove': 'add'](doms1[i].classList.contains('tools-setting') ? "disabled" : "lasso_disabled");
    }
  }
}
function getBinSizeList(){
  const binsizeContiner = makeElement('div',['binsize-container'],{});
  binsizeContiner.addEventListener('click',(e:Event)=>{
    e.stopPropagation();
  })
  const ul = makeElement('ul',['el-select-dropdown__list']);
  const binsize_auto = new ToolControl(viewer.isBinsizeAuto, {
    classes: ['binsize-auto-control'],
    label: i18n.binsizeAuto,
    isDownIcon: false,
    onChange: () => {
      // scalebar.style.display = viewer.showColorbar.value ? 'block' : 'none';
    }
  });
  binsize_auto.element.classList[viewer.binSize.value === 'cellbin' ? 'add': 'remove']('ng-common-disabled');
  binsize_auto.element.classList.add('binsize-auto-control');
  binsize_auto.element.style.height = '2.124rem';
  binsize_auto.element.style.display = 'none';
  if(allToolsButton.bin_list.length > 4)binsize_auto.element.style.display = 'flex';
  ul.appendChild(binsize_auto.element);
  (allToolsButton.bin_list as Array<string>).forEach((item: string)=>{
    const li = makeElement('li',['el-select-dropdown_item'],{},`${item}`);
    ul.appendChild(li);
    li.addEventListener('click',()=>{
      if(viewer.isBinsizeAuto.value || viewer.binSize.value === item)return
      changeBinSize(item);
    });
  });
  binsizeContiner.appendChild(ul);
  return binsizeContiner
}
export async function changeBinSize(item: string){
  if (dbStorageMoreWindow) {
    await dbStorageMoreWindow.update('layerName', {name: 'value', data: {href: item, value: [[],[]]}})
  }
  localStorage.setItem('layerName', new Date().toLocaleString());
  let old_json = JSON.parse(JSON.stringify(viewer.state.toJSON()));
  const binsize_auto = document.getElementsByClassName('binsize-auto-control');
  if(binsize_auto && binsize_auto[0])binsize_auto[0].classList.remove('ng-common-disabled');
  if (document.getElementById('gene-control-panel-button')?.classList.contains('lasso_disabled')) return;
  if (document.getElementsByClassName('tools-setting')[0]?.getAttribute('data-checked') === 'true') document.getElementsByClassName('tools-setting')[0]?.setAttribute('data-checked','false')
  viewer.initConfigState()
  // viewer.layout.restoreState('xy');
  const curr_bin = viewer.binSize.value;    //  存储当前的binsize，在切换binsize时，若没有item(binsize),则不变化binsize
  viewer.binSize.value = item
  viewer.emptyChunks = {};
  viewer.h5adfile_info_res = {};
  _check_image_Color.forEach(()=>_check_image_Color.pop());
  if(_check_image_Color.length > 0)_check_image_Color.forEach(()=>_check_image_Color.pop());
  isToolControl('remove');
  viewer.emptyChunks = {}
  viewer.binSize.value = item;
  umapVisible.visible = false
  // 切换binszie保持原来的图层勾选状态
  let layerState = {}
  let is_gef_visible: boolean = false;
  let is_gef_visible_name: string = ''; // 没有默认展开的热图时，默认展开第一个gef
  viewer.layerManager.managedLayers.forEach((layer:any) => {
    layerState[layer.name] = layer.visible    //  只有annotation和cellbin保留
    if(layer.type !== 'annotation' && layer.type !== 'segmentation')layerState[layer.name] = layer.visible    //  只有annotation和cellbin保留
    if(layer.heatmap){
      layerState[layer.name] = layer.visible;
      is_gef_visible_name = is_gef_visible_name ? is_gef_visible_name : layer.name
      if(layer.visible && !is_gef_visible)is_gef_visible = true
    }
    if (layer.type === 'annotation') {
      layer.layer.invalidateCache()
    }
  });
  if(!is_gef_visible)layerState[is_gef_visible_name] = true;
  const onlyHeatmap = viewer.layerManager.getLayerVisible(['ssDNA','DAPI','mask'])
  let newLayers = viewer.state.toJSON();
  viewer.isLegendOfImage.value = true;
  if (item === 'cellbin') {
    viewer.saveSelectedKeyVal = {};
    isToolControl('add', ['tools-undo', 'measure_btn'])
    viewer.showLegend.value = true;
    newLayers.layers.forEach((item:any, index: number) => {
      if(item.genetype === 'proteinimage')newLayers.layers.splice(index, 1)
    });
    viewer.genePanelState.reset();
    viewer.showParameter.value = false;
    layerState = {};
    viewer.proteinMergeColor.value = false;
  }
  layerState['binsize'] = curr_bin;
  viewer.genePanelState.reset();
  viewer.binSize.value = item;
  const param: getNgLayersParam = {
    layerTree: viewer.layerTree,
    binsize: item,
    json: newLayers,
    options: {sn: String(allToolsButton.sn), source: String(allToolsButton.source)},
    layerState: layerState,
    isChangeBinsize: true
  }
  const is_gene_or_protein = {
    str: JSON.stringify(viewer?.saveSelectedKeyVal) !== '{}' && viewer.proteinMergeColor.value ? 'mutil' : JSON.stringify(viewer?.saveSelectedKeyVal) !== '{}' && !viewer.proteinMergeColor.value ? 'heatmap' : undefined,
    binsziemaxvalue: viewer.binsizeMaxvalue
  }
  await getNgLayers(param, is_gene_or_protein, local_host_SN_flag, allToolsButton, _check_image_Color);
  let json = viewer.state.toJSON();
  const crossSectionScale = viewer.state.toJSON().crossSectionScale;
  json.layers = !allToolsButton.is_match_binsize ? json.layers : newLayers.layers
  json.imageTools = {...json.imageTools, ...newLayers.imageTools}
  json.crossSectionScale = crossSectionScale;
  json.layout = 'xy'
  if(allToolsButton.is_match_binsize){
    viewer.state.restoreState(json);
  }else{
    viewer.state.restoreState(old_json,false);
  }
  if(!onlyHeatmap){
    let dimension = json.dimensions;
    dimension['z'] = json.dimensions.x;
    viewer.coordinateSpace.restoreState(dimension);
  }
  viewer.configStorage.clear();
  viewer.configDialog?.close();
  viewer.genePanelState.reset();
  if(document.body.querySelector(".can_zoom_move_container")) document.body.removeChild((document.body.querySelector(".can_zoom_move_container") as HTMLDivElement));
  if(document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]){
    document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0].setAttribute('data-checked','false')
  }
  viewer.configStorage.registConfig(i18n.color_bar, document.getElementsByClassName('tool-control-'+i18n.color_bar.replace(/\s*/g,""))[0] as HTMLElement, i18n.color_bar);
  allToolsButton.is_match_binsize = true;
  if(local_href_param?.layername?.toLocaleLowerCase().includes('umap'))setvisibleLayer();
  viewer.needRestoreHeatMapFilter = true;
  viewer.imgFadeNeeded.value = false;
}

async function submitIF(){
  const {ssDNASize, IFlayerSize, ssDNAArr, IFMarkArr} = IFSubmitBox();
  if ((ssDNAArr.length + IFMarkArr.length) < 8) {
    new message('Please mark 8 points！', 'error')
    return;
  } else {
    exit_if();
    const data = {
      if: {
        size: [IFlayerSize[0], IFlayerSize[1]],
        chipRect: IFMarkArr,
        rot: viewer.image_rotate.length
      },
      ssdna: {
        size: [ssDNASize[0], ssDNASize[1]],
        chipRect: ssDNAArr
      }
    }
    const param = {
      data: data,
      username: localStorage.getItem('username'),
      dataId: local_dataId
    }
    let req = await (async (): Promise<any> => {
      return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/rawimage/register_task`, { method: 'POST', body: JSON.stringify(param), headers: { "Content-Type": "application/json" } }, responseJson);
    })();
    new message(`The task ID is ${req.data.taskid}`, 'success')
  }
}

function exit_if(){
  const {IFstaticAnnotations, ssDNAAnnotationSource} = IFSubmitBox();
  const button = document.getElementsByClassName('neuroglancer-icon-IF-control')[0] as HTMLElement;
  let referenceValue: any = {};  //  staticAnnotations!.references为map对象，需存储references
  let IFreferenceValue: any = {};
  if (ssDNAAnnotationSource) {
    for (let value_ssDNA of ssDNAAnnotationSource?.references) {
      referenceValue[value_ssDNA[0]] = value_ssDNA[1];
    }
    for (let i_ssDNA in referenceValue) {
      ssDNAAnnotationSource?.delete(referenceValue[i_ssDNA]);
    }
  }
  if (IFstaticAnnotations) {
    for (let value of IFstaticAnnotations!.references) {
      IFreferenceValue[value[0]] = value[1];
    }
    for (let i in IFreferenceValue) {
      IFstaticAnnotations?.delete(IFreferenceValue[i]);
    }
  }
  let json_ = viewer.state.toJSON();
  json_.layout = 'xy';
  json_.layers = viewer.layerCloneObj.layer;
  json_.imageTools.showColorbar = true;
  json_.selectedLayer = {};
  viewer.state.restoreState(json_);
  button.dataset.checked = 'false';
  viewer.image_rotate = '';    //  清空旋转标志位
  button.setAttribute('title', 'Start IF registration');
  isToolControl('remove', ['neuroglancer-icon-IF-control']);
  (document.getElementsByClassName('neuroglancer-viewer-if-register-operate-row')[0] as HTMLElement).style.display = 'none';
  (document.getElementsByClassName('neuroglancer-viewer-if-register-row')[0] as HTMLElement).style.display = 'none';
  isSelectView(true, ['tools-setting', 'layer-view']);
}

export class SwitchV2Info extends Overlay {
  constructor(public viewer: Viewer, info: string, confirmFn: Function, cancelFn: Function, value?: string) {
    super();
    this.content.classList.add('state_operate_dialog');
    const element = makeElement('div',[],{'style': 'overflow-y: auto; height: 100%'});
    if(!value){
      this.content.innerHTML += `<h3 style="text-align: center">${i18n.notice}</h3><div class="dialog_container" style="text-align: inherit;">${info}</div>`;
      this.content.innerHTML += `
      <div class="footer">
      <button class="cancel_btn">${i18n.cancel}</button>
      <button class="submit_btn">${i18n.confirm}</button>
      </div>`
    }else{
      this.content.style.height = '26.5rem';
      this.content.style.width = '35rem';
      this.content.appendChild(element)
      element.innerHTML += `<h3 style="text-align: center">Upgrade Notice</h3><div class="dialog_container" style="text-align: inherit;">${info}</div>`;
      element.innerHTML += `<br><input class="ng-common-checkbox switchV2_checkbox" type="checkbox" /><span>Don't show this prompt for me again.</span>`
      element.innerHTML += `
        <div class="footer" style="position: relative">
          <button class="submit_btn" style="margin: 0 3rem 0 calc(100% - 8rem)">${i18n.confirm}</button>
        </div>
      `;
    }
    if(value){
      (document.getElementsByClassName('switchV2_checkbox')[0] as HTMLInputElement).checked = localStorage.getItem('switch') === 'true';
      this.registerEventListener(document.getElementsByClassName('switchV2_checkbox')[0], 'change', (e:Event)=>{
        if((e.target as HTMLInputElement).checked){
          localStorage.setItem('switch', 'true');
        }else{
          localStorage.setItem('switch', 'false');
        }
      });
    }
    this.content.appendChild(this.buttonClose);
    this.registerEventListener(this.buttonClose, 'click', () => {
      cancelFn();
      this.dispose();
    });
    if(!value){
      this.registerEventListener(this.content.getElementsByClassName("cancel_btn")[0], 'click', () => {
        cancelFn();
        this.dispose();
      })
    }
    this.registerEventListener(this.content.getElementsByClassName("submit_btn")[0], 'click', () => {
      confirmFn();
      this.dispose();
    })
  }
}
export function deleteSignpoint(){
  const layer = viewer.layerManager.managedLayers.filter((item:any)=>item.name.includes('transform_'))[0];
  // const layer = viewer.layerManager.getLayerByName('transform_'+register);
  if( !( (layer as ManagedUserLayer).layer as ImageUserLayer).dataSources[0].loadState?.error ){
    const source:LoadedDataSubsource = (( (layer as ManagedUserLayer).layer as ImageUserLayer).dataSources[0].loadState as LoadedLayerDataSource).subsources[2];
    if(source?.subsourceEntry?.subsource?.staticAnnotations?.references){
      const annoSource = (((layer as ManagedUserLayer).layer as ImageUserLayer)?.dataSources[0]?.loadState_ as LoadedLayerDataSource)?.subsources[2].subsourceEntry.subsource.staticAnnotations as AnnotationSource;
      for (let value_ssDNA of annoSource.references) {
        console.log(value_ssDNA)
        source.subsourceEntry.subsource.staticAnnotations?.delete(value_ssDNA[1]);
      }
      annoSource.references = new Map();
    }
  }
  isSelectView(true, ['tools-setting', 'layer-view']);
}

// flag: 控制是否disabled， value控制配准模板（配准模板可以导出），lasso控制lasso时可以导入lasso数据
export function disableStatus(flag: boolean, value?: string | undefined, lasso?: boolean | undefined){
  if(lasso !== undefined)document.getElementsByClassName('all-load')[0]?.classList[lasso ? 'add': 'remove']("disabled");;
  if(lasso === undefined)document.getElementsByClassName('all-load')[0]?.classList[flag ? 'add': 'remove']("disabled");;
  document.getElementsByClassName('status-save')[0]?.classList[flag ? 'add': 'remove']("disabled");;
  if(!value)document.getElementsByClassName('status-export')[0]?.classList[flag ? 'add': 'remove']("disabled");;
}

export async function check_balance(){
  if(_param.flag === 'default')return true
  let req = async (): Promise<any> => {
    return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/check_balance`, {}, responseJson);
  };
  const res = await req();
  if(res.code === 0){
    if(!res.data.flag)new message(res.msg ,'error');
    return res.data.flag
  }else{
    new message(res.msg ,'error');
    return false
  }
}

function jishu(num: number) {
  if(num !== 0){ 
    const p = Math.floor( Math.log( num ) / Math.LN10 );
    const n = num * (10 ** -p); 
    return `${n.toFixed(2)}e${p - 12}`;
  }
  return 0;
}
